import React, { useState, useEffect, useRef, lazy, Suspense, useMemo } from 'react'
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  ArrowLeft,
  BookOpen,
  Clock,
  Target,
  Brain,
  Play,
  ChevronLeft,
  ChevronRight,
  Bookmark,
  Share2,
  Download,
  Eye,
  EyeOff,
  Video,
  Gamepad2,
  AlertTriangle,
  X,
  Database,
  Sparkles
} from 'lucide-react'
import InfographicTab from '../../components/infographic/InfographicTab'
import Navigation from '../../components/common/Navigation'
import ContentRenderer from '../../components/ContentRenderer/ContentRenderer'
import LoadingSpinner from '../../components/common/LoadingSpinner'
import QuizInterface from '../../components/quiz/QuizInterface'
import Footer from '../../components/common/Footer'
import { InteractiveContentRenderer } from '../../components/interactive'
import toast from 'react-hot-toast'
import { bankQuestionService, BankQuizData } from '../../services/bankQuestionService_moi'
import QuestionStats from '../../components/quiz/QuestionStats'
import { getYouTubeUrl } from '../../data/youtubeLinks'
import { ContentServiceSimple } from '../../services/content/ContentServiceSimple'
import { LessonStatsService } from '../../services/stats/LessonStatsService'
import LessonStatsDisplay from '../../components/stats/LessonStatsDisplay'
import { VideoService } from '../../services/video/VideoService'
import { emailServicePHP } from '../../services/email/EmailServicePHP'
import { useAuth } from '../../contexts/AuthContext'
import { teacherContentService } from '../../services/teacher/TeacherContentService'
import { databaseAvailabilityService } from '../../services/teacher/DatabaseAvailabilityService'
import { useStudyTracking } from '../../hooks/useStudyTracking'
import { PhysicsSurvivalRunner, GameHistory } from '../../components/game'
import { hasGameQuestions } from '../../data/gameQuestions'
import { GeoGebraService } from '../../services/video/GeoGebraService' // ✅ Import Service
import GeoGebraSimulation from '../../components/interactive/shared/simulations/GeoGebraSimulation' // ✅ Import Component

interface LessonData {
  id: number
  tieuDe: string
  moTa: string
  metadata: {
    thoiGian: number
    doKho: string
    chuDe: string[]
    mucTieu: string[]
  }
  youtubeUrl?: string
  noiDung: {
    phanMoDau: {
      tieuDe: string
      noiDung: any[]
    }
    phanNoiDung: {
      tieuDe: string
      noiDung: any[]
    }
    phanCuoi: {
      tieuDe: string
      noiDung: any[]
    }
  }
}

interface QuizData {
  id: string
  tieuDe: string
  moTa: string
  metadata: {
    thoiGian: number
    soCauHoi: number
    diemDat: number
    doKho: string
    chuDe: string[]
  }
  cauHoi: Array<{
    id: string
    type: string
    doKho: string
    diem: number
    cauHoi: string
    luaChon?: string[]
    dapAnDung: any
    giaiThich: string
    goiY: string[]
  }>
}

interface BaiHocSections {
  lyThuyet: {
    noiDung: any[]
    videoUrl?: string
  }
  video: {
    youtubeUrl: string
    moTa: string
  }
  tracNghiem: {
    cauHoi: any[]
    thoiGian: number
    metadata: any
  }
  moPhong: {
    ten: string
    moTa: string
    url: string
  }
  infographic?: {
    url: string
    description: string
  }
}

const XemBaiHoc: React.FC = () => {
  const { idLop, idChuong, idBai } = useParams<{ idLop: string; idChuong: string; idBai: string }>()
  const [searchParams] = useSearchParams()
  const teacherId = searchParams.get('teacher') // Lấy teacher ID từ query param

  const extractNumericId = (value?: string, fallback = 1) => {
    if (!value) return fallback
    const match = value.match(/(\d+)/)
    return match ? parseInt(match[1], 10) : fallback
  }

  const lopNumber = extractNumericId(idLop, 10)
  const chuongNumber = extractNumericId(idChuong, 1)
  const baiNumber = extractNumericId(idBai, 1)
  const navigate = useNavigate()
  const { user, checkStudentInfo, checkUserInfo, isLoading: authLoading, loginWithGoogle } = useAuth()

  const [lessonData, setLessonData] = useState<LessonData | null>(null)
  const [quizData, setQuizData] = useState<QuizData | null>(null)
  const [bankQuizData, setBankQuizData] = useState<BankQuizData | null>(null)
  const [baiHocSections, setBaiHocSections] = useState<BaiHocSections | null>(null)
  const [teacherSimulationContent, setTeacherSimulationContent] = useState<any>(null)
  const [teacherInfographicContent, setTeacherInfographicContent] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [currentSection, setCurrentSection] = useState<'lyThuyet' | 'video' | 'tracNghiem' | 'moPhong' | 'infographic'>('lyThuyet')
  const [showTableOfContents, setShowTableOfContents] = useState(false)
  const [bookmarked, setBookmarked] = useState(false)
  const [contentErrors, setContentErrors] = useState<Array<{ error: Error, item: any, severity: 'critical' | 'warning' }>>([])
  const [showQuizInterface, setShowQuizInterface] = useState(false)
  const [showLoginModal, setShowLoginModal] = useState(false) // ✅ Modal yêu cầu đăng nhập
  const [quizKey, setQuizKey] = useState(0) // Key để force remount QuizInterface khi cần reset
  const [isQuizFullscreen, setIsQuizFullscreen] = useState(false)
  const [overallProgress, setOverallProgress] = useState(0)
  const [SimulationComponent, setSimulationComponent] = useState<React.ComponentType<any> | null>(null)
  const [simulationLoading, setSimulationLoading] = useState(false)
  const [simulationError, setSimulationError] = useState<string | null>(null)
  const [showPhysicsSurvivalGame, setShowPhysicsSurvivalGame] = useState(false)
  const [showSummaryPanel, setShowSummaryPanel] = useState(false)
  const [summaryAcknowledged, setSummaryAcknowledged] = useState(false)
  const [activeSummarySection, setActiveSummarySection] = useState<string | null>(null)
  const summaryTriggerRef = useRef<HTMLDivElement | null>(null)
  const moDauSectionRef = useRef<HTMLDivElement | null>(null)
  const noiDungSectionRef = useRef<HTMLDivElement | null>(null)
  const phanCuoiSectionRef = useRef<HTMLDivElement | null>(null)

  // ✅ NEW: Track if we have already checked user info to avoid repeated prompts
  const [hasCheckedUserInfo, setHasCheckedUserInfo] = useState(false)

  const summaryPoints = useMemo(() => {
    const objectives = lessonData?.metadata?.mucTieu || []
    if (objectives.length >= 3) {
      return objectives.slice(0, Math.min(5, objectives.length))
    }

    const fallback: string[] = []
    if (lessonData?.noiDung?.phanMoDau?.tieuDe) {
      fallback.push(`Điểm chính: ${lessonData.noiDung.phanMoDau.tieuDe}`)
    }
    if (lessonData?.noiDung?.phanNoiDung?.tieuDe) {
      fallback.push(`Ghi nhớ: ${lessonData.noiDung.phanNoiDung.tieuDe}`)
    }
    if (lessonData?.noiDung?.phanCuoi?.tieuDe) {
      fallback.push(`Tổng kết: ${lessonData.noiDung.phanCuoi.tieuDe}`)
    }

    return fallback.slice(0, Math.min(5, fallback.length))
  }, [lessonData?.metadata?.mucTieu, lessonData?.noiDung])

  // ✅ Handle section change with user info check and login requirement
  const handleSectionChange = async (section: 'lyThuyet' | 'video' | 'tracNghiem' | 'moPhong' | 'infographic') => {
    console.log('🔄 [handleSectionChange] Changing to section:', section);

    // ✅ Nếu có teacherId trong URL, không yêu cầu đăng nhập (xem công khai)
    if (teacherId) {
      console.log('👨‍🏫 [handleSectionChange] Teacher content view - skipping login requirement');
      setCurrentSection(section);
      return;
    }

    // Check user info and login requirement for lyThuyet, tracNghiem and infographic
    if (section === 'lyThuyet' || section === 'tracNghiem' || section === 'infographic') {
      console.log('🔍 [handleSectionChange] Checking user info and login for', section);

      // ✅ Bước 1: Yêu cầu đăng nhập với Google nếu chưa đăng nhập
      if (!user) {
        console.log('⚠️ [handleSectionChange] User not logged in, showing login modal');
        setShowLoginModal(true); // Hiện modal đăng nhập
        return; // Không đổi section
      }

      console.log('📊 [handleSectionChange] User data:', {
        id: user.id,
        email: user.email,
        role: user.role,
        hasStudentInfo: !!user.studentInfo,
        hasTeacherInfo: !!user.teacherInfo,
        hasChecked: hasCheckedUserInfo
      });

      // ✅ FIX: Nếu là giáo viên (role === 'teacher') -> KHÔNG cần bổ sung thông tin
      if (user.role === 'teacher') {
        console.log('👨‍🏫 [handleSectionChange] User is teacher, skipping info check');
        setHasCheckedUserInfo(true); // Mark as checked
      }
      // ✅ FIX: Nếu đã check rồi -> KHÔNG check lại
      else if (!hasCheckedUserInfo) {
        // ✅ Bước 2: Nếu đã đăng nhập, kiểm tra thông tin user từ database WordPress
        // ⚠️ TEMPORARY: Disable user info check as requested
        /*
        const needsInfo = await checkUserInfo()
        console.log('🔍 [handleSectionChange] Needs info?', needsInfo);
        
        if (needsInfo) {
          console.log('⚠️ [handleSectionChange] User needs info or not in database, modal will be shown, not changing section');
          // Modal bổ sung thông tin sẽ được hiện bởi checkUserInfo
          return
        } else {
          console.log('✅ [handleSectionChange] User has complete info, marking as checked');
          setHasCheckedUserInfo(true); // ✅ Mark as checked successfully
        }
        */
        // ⚠️ TEMPORARY: Force allow
        console.log('⚠️ [handleSectionChange] User info check TEMPORARILY DISABLED');
        setHasCheckedUserInfo(true);
      } else {
        console.log('✅ [handleSectionChange] User info already checked previously');
      }
    }
    // All good, change section
    console.log('✅ [handleSectionChange] Changing section to', section);
    setCurrentSection(section)
  }

  // ✅ FIX: Load teacher databases sớm khi có teacherId để tránh treo
  const [teacherDatabasesPreloaded, setTeacherDatabasesPreloaded] = useState<Record<'TN' | 'DS' | 'TL', Uint8Array | null> | null>(null)

  useEffect(() => {
    const preloadTeacherDatabases = async () => {
      if (!teacherId) {
        setTeacherDatabasesPreloaded(null)
        return
      }

      // Extract lesson parameters
      const lopNum = parseInt(idLop || '10')
      const chuongNum = parseInt((idChuong || '1').replace('chuong-', '').split('-')[0])
      const baiNum = parseInt((idBai || '1').replace('bai-', '').split('-')[0])

      let lopParam = String(lopNum)
      let chuongParam = String(chuongNum)
      let baiParam = String(baiNum)

      console.log('🚀 [Preload] Starting to preload teacher databases...', { teacherId, lopParam, chuongParam, baiParam })

      try {
        // ✅ FIX: Load với timeout ngắn hơn và fallback nhanh
        // ✅ FIX: Check database availability trước (sử dụng cache)
        const availability = await databaseAvailabilityService.checkDatabaseAvailability(teacherId, lopParam, chuongParam, baiParam)

        let dbs: Record<'TN' | 'DS' | 'TL', Uint8Array | null> = { TN: null, DS: null, TL: null }

        // Chỉ load nếu có database từ server
        if (availability.hasServerDatabase) {
          try {
            dbs = await teacherContentService.getTeacherDatabases(teacherId, lopParam, chuongParam, baiParam, 3000)
          } catch (e) {
            console.warn('⚠️ [Preload] Failed to load teacher databases:', e)
          }
        } else {
          console.log('⚠️ [Preload] No server database available (from cache), skipping preload')
        }

        if ((dbs.TN && dbs.TN.length > 0) || (dbs.DS && dbs.DS.length > 0) || (dbs.TL && dbs.TL.length > 0)) {
          console.log('✅ [Preload] Teacher databases preloaded successfully:', {
            TN: dbs.TN ? `${dbs.TN.length} bytes` : 'null',
            DS: dbs.DS ? `${dbs.DS.length} bytes` : 'null',
            TL: dbs.TL ? `${dbs.TL.length} bytes` : 'null'
          })
          setTeacherDatabasesPreloaded(dbs)
        } else {
          console.log('⚠️ [Preload] No teacher databases found, will use system')
          setTeacherDatabasesPreloaded({ TN: null, DS: null, TL: null })
        }
      } catch (error) {
        console.warn('⚠️ [Preload] Error preloading teacher databases:', error)
        setTeacherDatabasesPreloaded({ TN: null, DS: null, TL: null })
      }
    }

    preloadTeacherDatabases()
  }, [teacherId, idLop, idChuong, idBai])

  // ✅ Preload system question counts (background)
  useEffect(() => {
    if (teacherId) return // Teacher mode handled by preloadTeacherDatabases

    const lopNum = parseInt(idLop || '10')
    const chuongNum = parseInt((idChuong || '1').replace('chuong-', '').split('-')[0])
    const baiNum = parseInt((idBai || '1').replace('bai-', '').split('-')[0])

    let lopParam = String(lopNum)
    let chuongParam = String(chuongNum)
    let baiParam = String(baiNum)

    console.log('🚀 [Preload] Starting to preload system question stats...', { lopParam, chuongParam, baiParam })

    // Call getQuestionCounts to populate cache
    bankQuestionService.getQuestionCounts(lopParam, chuongParam, baiParam)
      .then(counts => console.log('✅ [Preload] System stats preloaded:', counts))
      .catch(err => console.warn('⚠️ [Preload] Error preloading system stats:', err))

  }, [teacherId, idLop, idChuong, idBai])

  // ✅ Check user info when page loads and currentSection is lyThuyet or tracNghiem
  useEffect(() => {
    const checkOnLoad = async () => {
      // ✅ Nếu có teacherId trong URL, không yêu cầu đăng nhập (xem công khai)
      if (teacherId) {
        console.log('👨‍🏫 [useEffect] Teacher content view - skipping login requirement');
        return;
      }

      // Only check if currentSection requires user info
      if (currentSection === 'lyThuyet' || currentSection === 'tracNghiem') {
        // ✅ Bước 1: Yêu cầu đăng nhập với Google nếu chưa đăng nhập
        if (!user) {
          console.log('⚠️ [useEffect] User not logged in for section:', currentSection);
          setShowLoginModal(true); // Hiện modal đăng nhập
          return;
        }

        // ✅ FIX: Nếu là giáo viên -> skip
        if (user.role === 'teacher') {
          console.log('👨‍🏫 [useEffect] User is teacher, skipping info check');
          setHasCheckedUserInfo(true);
          return;
        }

        // ✅ FIX: Nếu đã check rồi -> skip
        if (hasCheckedUserInfo) {
          console.log('✅ [useEffect] User info already checked, skipping');
          return;
        }

        // ✅ Bước 2: Nếu đã đăng nhập, kiểm tra thông tin user
        console.log('🔍 [useEffect] Page loaded, checking user info for section:', currentSection);
        // ⚠️ TEMPORARY: Disable user info check as requested
        /*
        const needsInfo = await checkUserInfo();
        if (needsInfo) {
          console.log('⚠️ [useEffect] User needs info, modal will be shown');
          // Modal bổ sung thông tin sẽ được hiện bởi checkUserInfo
        } else {
          console.log('✅ [useEffect] User info complete, marking as checked');
          setHasCheckedUserInfo(true);
        }
        */
        // ⚠️ TEMPORARY: Force allow
        console.log('⚠️ [useEffect] User info check TEMPORARILY DISABLED');
        setHasCheckedUserInfo(true);
      }
    };

    // Only check after page is fully loaded (not loading) and auth is ready
    if (!loading && !authLoading) {
      // Use a small delay to ensure all state is ready
      const timer = setTimeout(() => {
        checkOnLoad();
      }, 100);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, user?.id, currentSection]); // Only depend on user.id, not the whole user object

  // ✅ Đảm bảo modal đăng nhập luôn hiện nếu chưa đăng nhập và đang ở section yêu cầu đăng nhập
  // ⚠️ CHỈ hiện modal khi CHẮC CHẮN chưa đăng nhập (không phải đang loading)
  // ✅ KHÔNG yêu cầu đăng nhập nếu có teacherId (xem công khai)
  useEffect(() => {
    if (teacherId) {
      // Xem teacher content - không yêu cầu đăng nhập
      return;
    }
    if ((currentSection === 'lyThuyet' || currentSection === 'tracNghiem' || currentSection === 'infographic') && !user && !authLoading && !showLoginModal) {
      console.log('⚠️ [useEffect] Ensuring login modal is shown (user not authenticated)');
      setShowLoginModal(true);
    }
  }, [currentSection, user, authLoading, showLoginModal, teacherId]);

  // ✅ Ref cho Google Sign-In button container
  const googleButtonRef = useRef<HTMLDivElement>(null);
  // ✅ Track việc initialize Google OAuth để tránh initialize lại
  const isGoogleOAuthInitialized = useRef(false);
  // ✅ Track timeout để cleanup
  const initTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // ✅ Xử lý Google Sign-In khi modal đăng nhập được hiện
  useEffect(() => {
    // Cleanup timeout khi unmount
    return () => {
      if (initTimeoutRef.current) {
        clearTimeout(initTimeoutRef.current);
        initTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!showLoginModal) {
      // Reset flag khi modal đóng để có thể initialize lại khi mở lại
      isGoogleOAuthInitialized.current = false;
      return;
    }

    // ✅ Đảm bảo container tồn tại trước khi bắt đầu
    // Sử dụng requestAnimationFrame để đảm bảo DOM đã render
    let retryCount = 0
    const MAX_RETRIES = 10 // Tối đa 10 lần retry (1 giây)

    const checkContainer = () => {
      if (!googleButtonRef.current) {
        retryCount++
        if (retryCount < MAX_RETRIES) {
          console.warn(`⚠️ [XemBaiHoc] Button container not found, retrying... (${retryCount}/${MAX_RETRIES})`);
          // Retry sau 100ms
          setTimeout(checkContainer, 100);
          return;
        } else {
          console.error('❌ [XemBaiHoc] Button container not found after max retries, giving up');
          return;
        }
      }

      console.log('🔍 [XemBaiHoc] Container found, starting initialization...');

      // Initialize Google OAuth first, then render button
      const initializeAndRender = async () => {
        // ✅ DEBUG: Log environment và state
        const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
        console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
        console.log('🔍 [XemBaiHoc] DEBUG - Starting Google Sign-In initialization');
        console.log('📊 Environment check:', {
          hasClientId: !!clientId,
          clientIdLength: clientId?.length || 0,
          clientIdPreview: clientId ? `${clientId.substring(0, 20)}...` : 'MISSING',
          isTestClientId: clientId === 'test-client-id',
          hasGoogle: !!window.google,
          hasGoogleAccounts: !!(window.google && window.google.accounts),
          hasGoogleAccountsId: !!(window.google && window.google.accounts && window.google.accounts.id),
          containerExists: !!googleButtonRef.current,
          modalVisible: showLoginModal
        });
        console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');

        // Kiểm tra lại container trước khi tiếp tục
        if (!googleButtonRef.current) {
          console.error('❌ [XemBaiHoc] Container lost during initialization');
          return;
        }

        // Kiểm tra client_id
        if (!clientId || clientId === 'test-client-id') {
          console.error('❌ [XemBaiHoc] VITE_GOOGLE_CLIENT_ID is missing or invalid!');
          console.error('📊 Client ID value:', clientId);
          if (googleButtonRef.current) {
            googleButtonRef.current.innerHTML = `
              <div class="text-center p-4">
                <p class="text-sm text-red-500 mb-2">Lỗi cấu hình: Google Client ID chưa được thiết lập</p>
                <p class="text-xs text-gray-500">Vui lòng liên hệ quản trị viên</p>
              </div>
            `;
          }
          return;
        }

        // Luôn clear để đảm bảo button được render mới
        googleButtonRef.current.innerHTML = '';

        try {
          console.log('🔄 [XemBaiHoc] Initializing Google Sign-In...');

          // First, load Google OAuth script if not already loaded
          if (!window.google || !window.google.accounts || !window.google.accounts.id) {
            console.log('📥 [XemBaiHoc] Loading Google OAuth script...');
            const script = document.createElement('script');
            script.src = 'https://accounts.google.com/gsi/client';
            script.async = true;
            script.defer = true;
            script.id = 'google-oauth-script';

            // Remove existing script if any
            const existingScript = document.getElementById('google-oauth-script');
            if (existingScript) {
              existingScript.remove();
            }

            await new Promise((resolve, reject) => {
              const timeout = setTimeout(() => {
                console.error('❌ [XemBaiHoc] Script load timeout after 10 seconds');
                reject(new Error('Script load timeout'));
              }, 10000);

              script.onload = () => {
                clearTimeout(timeout);
                console.log('✅ [XemBaiHoc] Google OAuth script loaded');
                // Wait a bit more for Google API to be ready
                setTimeout(() => {
                  if (window.google && window.google.accounts && window.google.accounts.id) {
                    console.log('✅ [XemBaiHoc] Google OAuth API is ready');
                    resolve(null);
                  } else {
                    console.warn('⚠️ [XemBaiHoc] Script loaded but API not ready yet');
                    resolve(null); // Continue anyway
                  }
                }, 500);
              };
              script.onerror = (error) => {
                clearTimeout(timeout);
                console.error('❌ [XemBaiHoc] Failed to load Google OAuth script:', error);
                console.error('📊 Script src:', script.src);
                console.error('📊 Network error details:', {
                  message: error instanceof Error ? error.message : 'Unknown error',
                  type: error instanceof Error ? error.name : typeof error
                });
                reject(error);
              };
              document.head.appendChild(script);
            });
          } else {
            console.log('✅ [XemBaiHoc] Google OAuth script already loaded');
          }

          // Wait a bit for Google OAuth to be ready
          let retries = 0;
          const maxRetries = 10;
          while (retries < maxRetries && (!window.google || !window.google.accounts || !window.google.accounts.id)) {
            console.log(`⏳ [XemBaiHoc] Waiting for Google OAuth API (attempt ${retries + 1}/${maxRetries})...`);
            await new Promise(resolve => setTimeout(resolve, 200));
            retries++;
          }

          // Kiểm tra lại container sau khi đợi
          if (!googleButtonRef.current) {
            console.error('❌ [XemBaiHoc] Container lost after waiting');
            return;
          }

          // Now check if Google OAuth is available
          if (window.google && window.google.accounts && window.google.accounts.id) {
            console.log('✅ [XemBaiHoc] Google OAuth available, initializing...');
            console.log('📊 Client ID being used:', clientId.substring(0, 30) + '...');

            // Chỉ initialize một lần
            if (!isGoogleOAuthInitialized.current) {
              try {
                window.google.accounts.id.initialize({
                  client_id: clientId,
                  callback: async (response: any) => {
                    console.log('✅ [XemBaiHoc] Google OAuth callback received');
                    try {
                      // Đăng nhập với Google
                      await loginWithGoogle(response);
                      console.log('✅ [XemBaiHoc] Login successful');

                      // Đóng modal đăng nhập
                      setShowLoginModal(false);

                      // ✅ Sau khi đăng nhập thành công, kiểm tra thông tin và hiện popup bổ sung nếu cần
                      setTimeout(async () => {
                        const needsInfo = await checkUserInfo();
                        if (needsInfo) {
                          console.log('⚠️ [XemBaiHoc] User needs info after login, modal will be shown');
                          // Modal bổ sung thông tin sẽ được hiện bởi checkUserInfo
                        }
                      }, 500);
                    } catch (error) {
                      console.error('❌ [XemBaiHoc] Error during login:', error);
                      toast.error('Đăng nhập thất bại. Vui lòng thử lại.');
                    }
                  },
                  auto_select: false,
                  cancel_on_tap_outside: true,
                });
                isGoogleOAuthInitialized.current = true;
                console.log('✅ [XemBaiHoc] Google OAuth initialized successfully');
              } catch (initError) {
                console.error('❌ [XemBaiHoc] Error during Google OAuth initialize:', initError);
                if (googleButtonRef.current) {
                  googleButtonRef.current.innerHTML = `
                    <div class="text-center p-4">
                      <p class="text-sm text-red-500 mb-2">Lỗi khởi tạo Google Sign-In</p>
                      <p class="text-xs text-gray-500">${initError instanceof Error ? initError.message : 'Unknown error'}</p>
                    </div>
                  `;
                }
                return;
              }
            } else {
              console.log('ℹ️ [XemBaiHoc] Google OAuth already initialized');
            }

            // LUÔN render button
            if (googleButtonRef.current) {
              console.log('🎨 [XemBaiHoc] Rendering Google Sign-In button...');
              console.log('📊 Container element:', {
                exists: !!googleButtonRef.current,
                id: googleButtonRef.current.id,
                className: googleButtonRef.current.className,
                innerHTML: googleButtonRef.current.innerHTML.substring(0, 50)
              });

              try {
                window.google.accounts.id.renderButton(googleButtonRef.current, {
                  theme: 'outline',
                  size: 'large',
                  width: 300,
                  text: 'signin_with',
                  shape: 'rectangular'
                });
                console.log('✅ [XemBaiHoc] Google Sign-In button render call completed');

                // Kiểm tra lại sau 1 giây xem button đã xuất hiện chưa
                setTimeout(() => {
                  if (googleButtonRef.current) {
                    const buttonNow = googleButtonRef.current.querySelector('div[role="button"]') ||
                      googleButtonRef.current.querySelector('button') ||
                      googleButtonRef.current.querySelector('iframe');
                    if (buttonNow) {
                      console.log('✅ [XemBaiHoc] Google Sign-In button confirmed visible');
                      console.log('📊 Button element:', {
                        tagName: buttonNow.tagName,
                        role: buttonNow.getAttribute('role'),
                        hasContent: buttonNow.innerHTML.length > 0
                      });
                    } else {
                      console.warn('⚠️ [XemBaiHoc] Google Sign-In button not found after render');
                      console.warn('📊 Container state:', {
                        innerHTML: googleButtonRef.current.innerHTML.substring(0, 200),
                        childrenCount: googleButtonRef.current.children.length,
                        hasIframe: !!googleButtonRef.current.querySelector('iframe')
                      });
                      // Retry render
                      console.log('🔄 [XemBaiHoc] Retrying button render...');
                      googleButtonRef.current.innerHTML = '';
                      try {
                        window.google.accounts.id.renderButton(googleButtonRef.current, {
                          theme: 'outline',
                          size: 'large',
                          width: 300,
                          text: 'signin_with',
                          shape: 'rectangular'
                        });
                        console.log('✅ [XemBaiHoc] Retry render completed');
                      } catch (retryError) {
                        console.error('❌ [XemBaiHoc] Retry render failed:', retryError);
                      }
                    }
                  }
                }, 1000);
              } catch (renderError) {
                console.error('❌ [XemBaiHoc] Error rendering button:', renderError);
                console.error('📊 Error details:', {
                  message: renderError instanceof Error ? renderError.message : 'Unknown error',
                  name: renderError instanceof Error ? renderError.name : typeof renderError,
                  stack: renderError instanceof Error ? renderError.stack : undefined
                });
                if (googleButtonRef.current) {
                  googleButtonRef.current.innerHTML = `
                    <div class="text-center p-4">
                      <p class="text-sm text-red-500 mb-2">Lỗi khi render Google Sign-In</p>
                      <p class="text-xs text-gray-500">${renderError instanceof Error ? renderError.message : 'Unknown error'}</p>
                    </div>
                  `;
                }
              }
            } else {
              console.error('❌ [XemBaiHoc] Container is null when trying to render button');
            }
          } else {
            console.error('❌ [XemBaiHoc] Google OAuth not available after wait');
            console.error('📊 Final state:', {
              hasGoogle: !!window.google,
              hasAccounts: !!(window.google && window.google.accounts),
              hasAccountsId: !!(window.google && window.google.accounts && window.google.accounts.id)
            });
            if (googleButtonRef.current) {
              googleButtonRef.current.innerHTML = `
                <div class="text-center p-4">
                  <p class="text-sm text-red-500 mb-2">Không thể tải Google Sign-In</p>
                  <p class="text-xs text-gray-500">Vui lòng tải lại trang hoặc kiểm tra kết nối mạng</p>
                </div>
              `;
            }
          }
        } catch (error) {
          console.error('❌ [XemBaiHoc] Error initializing Google Sign-In:', error);
          console.error('📊 Error details:', {
            message: error instanceof Error ? error.message : 'Unknown error',
            name: error instanceof Error ? error.name : typeof error,
            stack: error instanceof Error ? error.stack : undefined
          });
          if (googleButtonRef.current) {
            googleButtonRef.current.innerHTML = `
              <div class="text-center p-4">
                <p class="text-sm text-red-500 mb-2">Google Sign-In không khả dụng</p>
                <p class="text-xs text-gray-500">${error instanceof Error ? error.message : 'Unknown error'}</p>
              </div>
            `;
          }
        }
      };

      // Start initialization and rendering
      initializeAndRender();
    };

    // ✅ Sử dụng requestAnimationFrame để đảm bảo DOM đã render
    requestAnimationFrame(() => {
      // Thêm delay nhỏ để đảm bảo modal đã mount hoàn toàn
      setTimeout(checkContainer, 100);
    });

    return () => {
      if (initTimeoutRef.current) {
        clearTimeout(initTimeoutRef.current);
        initTimeoutRef.current = null;
      }
    };
  }, [showLoginModal, loginWithGoogle, checkUserInfo]); // Thêm dependencies để đảm bảo callback luôn mới nhất

  const summarySectionMeta = useMemo(
    () => [
      {
        key: 'phanMoDau',
        title: lessonData?.noiDung?.phanMoDau?.tieuDe || 'Phần mở đầu'
      },
      {
        key: 'phanNoiDung',
        title: lessonData?.noiDung?.phanNoiDung?.tieuDe || 'Phần nội dung'
      },
      {
        key: 'phanCuoi',
        title: lessonData?.noiDung?.phanCuoi?.tieuDe || 'Phần cuối'
      }
    ],
    [lessonData?.noiDung?.phanMoDau?.tieuDe, lessonData?.noiDung?.phanNoiDung?.tieuDe, lessonData?.noiDung?.phanCuoi?.tieuDe]
  )

  const summaryActive = summaryPoints.length >= 3 && showSummaryPanel && !summaryAcknowledged && currentSection === 'lyThuyet' && !isQuizFullscreen

  const getSummaryHighlightClass = (key: string) => {
    if (!summaryActive) {
      return ''
    }
    return activeSummarySection === key
      ? 'ring-4 ring-blue-300 ring-offset-2 ring-offset-white shadow-[0_0_32px_rgba(59,130,246,0.35)] scale-[1.01] transition duration-500'
      : 'opacity-80 transition duration-300'
  }

  // ✅ NEW: Track completed sections và final stats modal
  const [completedSections, setCompletedSections] = useState<Set<string>>(new Set())
  const [showFinalStatsModal, setShowFinalStatsModal] = useState(false)

  // ✅ NEW: Track email sent status để tránh gửi nhiều lần
  const [emailSent, setEmailSent] = useState(false)

  // ✅ NEW: Track counts of 4 lesson components for progress calculation
  const [lessonComponentCounts, setLessonComponentCounts] = useState({
    interactiveQuestions: 0,
    videos: 0,
    quizzes: 0,
    simulations: 0
  })

  // ✅ FIX: Tạo lessonId duy nhất cho cả bài học
  const lessonId = `L${idLop}_C${idChuong}_B${idBai}`

  // ✅ PHASE 2: Study Tracking Hook (Optimized - less aggressive)
  // ⚠️ Tracking tự động chạy ở background, không sync với thanh tiến độ UI
  const {
    trackingData
    // updateProgress và completeLesson không sử dụng - tracking tự động
  } = useStudyTracking({
    lessonId,
    lessonTitle: lessonData?.tieuDe || 'Đang tải...',
    autoStart: !!lessonData, // ✅ CHỈ start khi đã có lessonData để tránh lưu "Đang tải..."
    updateInterval: 60000, // Update mỗi 60 giây (thay vì 10s)
    counts: lessonComponentCounts // ✅ Pass counts of 4 components
  });

  // ✅ PHASE 2: Sync progress CHỈ KHI có thay đổi đáng kể
  // ⚠️ COMMENTED OUT: Giữ nguyên thanh tiến độ chỉ hiển thị phần tương tác lý thuyết
  // Không sync overallProgress với tracking system để tránh conflict
  /*
  const lastSyncedProgress = useRef(0);
  
  useEffect(() => {
    if (overallProgress > 0 && user?.id) {
      const currentProgress = Math.floor(overallProgress);
      
      // Chỉ update khi progress thay đổi ít nhất 10%
      if (Math.abs(currentProgress - lastSyncedProgress.current) >= 10) {
        updateStudyProgress(currentProgress);
        lastSyncedProgress.current = currentProgress;
      }
      
      // Auto complete khi đạt 100%
      if (overallProgress >= 100 && lastSyncedProgress.current < 100) {
        completeStudyLesson();
        lastSyncedProgress.current = 100;
      }
    }
  }, [overallProgress, user?.id]);
  */

  // ✅ FIX: Khởi tạo lesson tracking và set totalQuestions NGAY KHI lessonData được load
  useEffect(() => {
    console.log(`🔍 DEBUG: useEffect triggered - lessonData:`, !!lessonData, 'lessonId:', lessonId)

    if (lessonData) {
      // ✅ STEP 1: Start lesson tracking TRƯỚC
      LessonStatsService.startLesson(lessonId)
      console.log(`📊 DEBUG: Started lesson tracking for ${lessonId}`)

      // ✅ STEP 2: Đếm TẤT CẢ 4 phần
      const countComponents = (content: any[]) => {
        const counts = {
          interactive: 0,
          videos: 0,
          quizzes: 0,
          simulations: 0
        };

        content.forEach((item: any) => {
          // Đếm interactive questions
          if (
            item?.type === 'interactive_question' ||
            item?.type === 'multiple_response_question' ||
            item?.type === 'sequence_question'
          ) {
            counts.interactive++;
          }
          // Đếm videos (YouTube embeds)
          else if (item?.type === 'video' || item?.type === 'youtube') {
            counts.videos++;
          }
          // Đếm simulations
          else if (
            item?.type === 'interactive_vt_graph' ||
            item?.type === 'interactive_oscillation' ||
            item?.type === 'video_simulation' ||
            item?.type === 'simulation'
          ) {
            counts.simulations++;
            // Đếm câu hỏi trong simulation
            if (item?.interactiveQuestions?.length > 0) {
              counts.interactive += item.interactiveQuestions.length;
            }
          }
        });

        return counts;
      };

      // Đếm tất cả sections
      const moDauCounts = countComponents(lessonData.noiDung.phanMoDau?.noiDung || []);
      const noiDungCounts = countComponents(lessonData.noiDung.phanNoiDung?.noiDung || []);
      const cuoiCounts = countComponents(lessonData.noiDung.phanCuoi?.noiDung || []);

      // Đếm quizzes từ tracNghiem
      const quizzesCount = (lessonData as any).tracNghiem ? 1 : 0; // Mỗi bài có 1 quiz section

      // Tổng hợp
      const totalCounts = {
        interactiveQuestions: moDauCounts.interactive + noiDungCounts.interactive + cuoiCounts.interactive,
        videos: moDauCounts.videos + noiDungCounts.videos + cuoiCounts.videos,
        quizzes: quizzesCount,
        simulations: moDauCounts.simulations + noiDungCounts.simulations + cuoiCounts.simulations
      };

      const totalQuestions = totalCounts.interactiveQuestions;

      console.log(`📊 DEBUG: Component counts:`, totalCounts);
      console.log(`📊 Total interactive questions: ${totalQuestions}`);
      console.log(`📊 Setting totalQuestions for lessonId: ${lessonId}`);

      LessonStatsService.setTotalQuestions(totalQuestions, lessonId);

      // ✅ Save counts to state for useStudyTracking
      setLessonComponentCounts(totalCounts);

      // ✅ DEBUG: Kiểm tra ngay sau khi set
      const stats = LessonStatsService.getStats(lessonId)
      console.log(`📊 DEBUG: Stats after setTotalQuestions:`, stats)
    } else {
      console.log(`📊 DEBUG: lessonData is null, skipping totalQuestions calculation`)
    }
  }, [lessonData, lessonId])

  // ✅ FIX: End lesson tracking when component unmounts
  useEffect(() => {
    return () => {
      // ✅ Gửi email khi rời khỏi trang (nếu đã học ít nhất 1 phút)
      const duration = LessonStatsService.getDuration(lessonId)
      if (duration > 60 && !emailSent && user?.email) {
        console.log('📧 User leaving page, sending theory completion email...')

        // Gửi email ngay lập tức (không async)
        const stats = LessonStatsService.getStats(lessonId)
        if (stats) {
          // ✅ FIX: Tính toán các metrics GIỐNG HỆT như trong modal
          const totalQuestions = stats.totalQuestions || 0
          const totalAnswered = stats.correctAnswers + stats.wrongAnswers
          const completedQuestions = totalAnswered
          const timeSpent = duration
          const sectionsCompleted = completedSections.size
          const totalSections = 3

          // ✅ Dùng CÙNG logic như LessonStatsService.getAccuracy()
          const accuracy = LessonStatsService.getAccuracy(lessonId)
          const progress = LessonStatsService.getProgress(lessonId)

          // Gửi email trực tiếp với metrics đã sửa
          emailServicePHP.notifyTheoryCompletion(
            user.name || 'Học sinh',
            user.email,
            lessonData?.tieuDe || 'Bài học',
            lessonId,
            {
              totalQuestions,
              completedQuestions,
              totalAnswered, // ✅ Thêm để rõ ràng
              correctAnswers: stats.correctAnswers, // ✅ Thêm số câu đúng
              wrongAnswers: stats.wrongAnswers, // ✅ Thêm số câu sai
              accuracy, // ✅ Accuracy đúng logic
              progress, // ✅ Progress đúng logic
              timeSpent,
              sectionsCompleted,
              totalSections
            }
          ).then(result => {
            if (result.success) {
              console.log('✅ Theory completion email sent on page exit:', result)
            } else {
              console.warn('⚠️ Theory completion email failed on page exit:', result.message)
            }
          }).catch(error => {
            console.error('❌ Error sending theory completion email on page exit:', error)
          })
        }
      }

      LessonStatsService.endLesson(lessonId)
      console.log(`📊 Lesson tracking ended for ${lessonId}`)
    }
  }, [lessonId, emailSent, user?.email, completedSections.size, lessonData?.tieuDe])

  // ✅ NEW: Theo dõi state trước đó của currentSection
  const previousSectionRef = React.useRef<'lyThuyet' | 'video' | 'tracNghiem' | 'moPhong' | 'infographic'>('lyThuyet')

  // ✅ NEW: Theo dõi khi user chuyển TAB - Gửi email khi rời khỏi tab lý thuyết
  useEffect(() => {
    const previousSection = previousSectionRef.current

    console.log(`🔍 TAB CHANGE DETECTED:`, {
      previousSection,
      currentSection,
      isLeavingTheory: previousSection === 'lyThuyet' && currentSection !== 'lyThuyet',
      duration: LessonStatsService.getDuration(lessonId),
      emailSent,
      hasUserEmail: !!user?.email,
      lessonDataExists: !!lessonData,
      completedSectionsSize: completedSections.size
    })

    // Detect khi rời khỏi tab lý thuyết
    if (previousSection === 'lyThuyet' && currentSection !== 'lyThuyet') {
      // User đã rời khỏi tab lý thuyết
      const duration = LessonStatsService.getDuration(lessonId)

      console.log(`✅ CONDITION CHECK:`, {
        duration,
        durationOK: duration > 30,
        emailSent,
        emailSentOK: !emailSent,
        hasEmail: !!user?.email,
        userName: user?.name,
        allConditionsMet: duration > 30 && !emailSent && user?.email
      })

      if (duration > 30 && !emailSent && user?.email) {
        console.log(`📧 User switched from theory tab to "${currentSection}" tab, sending completion email...`)

        // ✅ Inline email sending để tránh stale closure
        const sendEmailNow = async () => {
          if (emailSent || !user?.email) {
            console.log('📧 Email already sent or no user email, skipping...')
            return
          }

          try {
            console.log('📧 Preparing to send theory completion email...')

            // Lấy thống kê từ LessonStatsService
            const stats = LessonStatsService.getStats(lessonId)
            if (!stats) {
              console.log('❌ No stats found for theory completion email')
              return
            }

            // ✅ FIX: Tính toán các metrics GIỐNG HỆT như trong modal
            const totalQuestions = stats.totalQuestions || 0
            const totalAnswered = stats.correctAnswers + stats.wrongAnswers
            // ✅ completedQuestions = tổng số lần trả lời (có thể > totalQuestions nếu làm lại)
            const completedQuestions = totalAnswered
            const timeSpent = LessonStatsService.getDuration(lessonId)
            const sectionsCompleted = completedSections.size
            const totalSections = 3 // Mở đầu + Nội dung + Cuối

            // ✅ Dùng CÙNG logic như LessonStatsService.getAccuracy()
            const accuracy = LessonStatsService.getAccuracy(lessonId)
            const progress = LessonStatsService.getProgress(lessonId)

            console.log('📧 Theory completion stats (FIXED):', {
              totalQuestions,
              completedQuestions,
              totalAnswered,
              correctAnswers: stats.correctAnswers,
              wrongAnswers: stats.wrongAnswers,
              accuracy, // ✅ Đúng logic: correctAnswers / totalAnswered
              progress, // ✅ Đúng logic: totalAnswered / totalQuestions
              timeSpent,
              sectionsCompleted,
              totalSections,
              score: `${stats.correctAnswers}/${totalQuestions}` // Số câu đúng / Tổng câu
            })

            // Gửi email với metrics đã sửa
            const emailResult = await emailServicePHP.notifyTheoryCompletion(
              user.name || 'Học sinh',
              user.email,
              lessonData?.tieuDe || 'Bài học',
              lessonId,
              {
                totalQuestions,
                completedQuestions,
                totalAnswered, // ✅ Thêm để rõ ràng
                correctAnswers: stats.correctAnswers, // ✅ Thêm số câu đúng
                wrongAnswers: stats.wrongAnswers, // ✅ Thêm số câu sai
                accuracy, // ✅ Accuracy đúng logic
                progress, // ✅ Progress đúng logic
                timeSpent,
                sectionsCompleted,
                totalSections
              }
            )

            if (emailResult.success) {
              console.log('✅ Theory completion email sent successfully:', emailResult)
              setEmailSent(true)
              toast.success('📧 Đã gửi thông báo hoàn thành lý thuyết qua email')
            } else {
              console.warn('⚠️ Theory completion email failed:', emailResult.message)
            }
          } catch (error) {
            console.error('❌ Error sending theory completion email:', error)
          }
        }

        sendEmailNow()
      } else {
        console.log(`⚠️ Not sending email - conditions not met`)
      }
    }

    // Cập nhật previous section cho lần tiếp theo
    previousSectionRef.current = currentSection as any
    console.log(`🔄 Updated previousSectionRef to: ${currentSection}`)
  }, [currentSection, lessonId, emailSent, user?.email, user?.name, lessonData?.tieuDe, completedSections.size])

  // ✅ NEW: Hàm gửi email cho lý thuyết
  const sendTheoryCompletionEmail = async () => {
    if (emailSent || !user?.email) {
      console.log('📧 Email already sent or no user email, skipping...')
      return
    }

    try {
      console.log('📧 Preparing to send theory completion email...')

      // Lấy thống kê từ LessonStatsService
      const stats = LessonStatsService.getStats(lessonId)
      if (!stats) {
        console.log('❌ No stats found for theory completion email')
        return
      }

      // ✅ FIX: Tính toán các metrics GIỐNG HỆT như trong modal
      const totalQuestions = stats.totalQuestions || 0
      const totalAnswered = stats.correctAnswers + stats.wrongAnswers
      // ✅ completedQuestions = tổng số lần trả lời (có thể > totalQuestions nếu làm lại)
      const completedQuestions = totalAnswered
      const timeSpent = LessonStatsService.getDuration(lessonId)
      const sectionsCompleted = completedSections.size
      const totalSections = 3 // Mở đầu + Nội dung + Cuối

      // ✅ Dùng CÙNG logic như LessonStatsService.getAccuracy()
      const accuracy = LessonStatsService.getAccuracy(lessonId)
      const progress = LessonStatsService.getProgress(lessonId)

      console.log('📧 Theory completion stats (FIXED):', {
        totalQuestions,
        completedQuestions,
        totalAnswered,
        correctAnswers: stats.correctAnswers,
        wrongAnswers: stats.wrongAnswers,
        accuracy, // ✅ Đúng logic: correctAnswers / totalAnswered
        progress, // ✅ Đúng logic: totalAnswered / totalQuestions
        timeSpent,
        sectionsCompleted,
        totalSections,
        score: `${stats.correctAnswers}/${totalQuestions}` // Số câu đúng / Tổng câu
      })

      // Gửi email với metrics đã sửa
      const emailResult = await emailServicePHP.notifyTheoryCompletion(
        user.name || 'Học sinh',
        user.email,
        lessonData?.tieuDe || 'Bài học',
        lessonId,
        {
          totalQuestions,
          completedQuestions,
          totalAnswered, // ✅ Thêm để rõ ràng
          correctAnswers: stats.correctAnswers, // ✅ Thêm số câu đúng
          wrongAnswers: stats.wrongAnswers, // ✅ Thêm số câu sai
          accuracy, // ✅ Accuracy đúng logic
          progress, // ✅ Progress đúng logic
          timeSpent,
          sectionsCompleted,
          totalSections
        }
      )

      if (emailResult.success) {
        console.log('✅ Theory completion email sent successfully:', emailResult)
        setEmailSent(true)
        toast.success('📧 Đã gửi thông báo hoàn thành lý thuyết qua email')
      } else {
        console.warn('⚠️ Theory completion email failed:', emailResult.message)
      }
    } catch (error) {
      console.error('❌ Error sending theory completion email:', error)
    }
  }

  // ✅ NEW: Handle section completion - chỉ hiện modal khi hoàn thành TẤT CẢ
  const handleSectionComplete = (sectionName: string) => {
    console.log(`📊 Section "${sectionName}" completed!`)

    const newCompletedSections = new Set(completedSections)
    newCompletedSections.add(sectionName)
    setCompletedSections(newCompletedSections)

    console.log(`📊 Completed sections:`, Array.from(newCompletedSections))
    console.log(`📊 Total sections: 3, Completed: ${newCompletedSections.size}`)

    // ✅ Check nếu ĐÃ HOÀN THÀNH TẤT CẢ 3 PHẦN
    if (newCompletedSections.size === 3) {
      console.log(`🎉 ALL 3 SECTIONS COMPLETED! Showing final stats in 2 seconds...`)

      // ✅ Gửi email khi hoàn thành tất cả sections
      sendTheoryCompletionEmail()

      // End lesson và hiện modal sau 2 giây
      setTimeout(() => {
        LessonStatsService.endLesson(lessonId)
        setShowFinalStatsModal(true)
      }, 2000)
    }
  }

  // Mapping từ URL params sang đường dẫn thư mục
  const lessonSlugs = useMemo(() => {
    const lopMap: { [key: string]: string } = {
      '10': 'lop-10',
      '11': 'lop-11',
      '12': 'lop-12'
    }

    const chuongMapLop10: { [key: string]: string } = {
      '0': 'chuong-0-mo-dau',
      '1': 'chuong-1-dong-hoc',
      '2': 'chuong-2-dong-luc-hoc',
      '3': 'chuong-3-nang-luong-va-cong',
      '4': 'chuong-4-chuyen-dong-tron-va-bien-dang'
    }

    const chuongMapLop11: { [key: string]: string } = {
      '1': 'chuong-1-dao-dong',
      '2': 'chuong-2-song',
      '3': 'chuong-3-dien-truong',
      '4': 'chuong-4-dong-dien-mach-dien',
      '5': 'chuong-5-tu-truong'
    }

    const chuongMapLop12: { [key: string]: string } = {
      '1': 'chuong-1-vat-li-nhiet',
      '2': 'chuong-2-khi-li-tuong',
      '3': 'chuong-3-tu-truong',
      '4': 'chuong-4-vat-li-hat-nhan'
    }

    const lop = lopMap[idLop || ''] || `lop-${idLop}`
    let chuongMap: { [key: string]: string } = {}
    if (idLop === '10') chuongMap = chuongMapLop10
    else if (idLop === '11') chuongMap = chuongMapLop11
    else if (idLop === '12') chuongMap = chuongMapLop12

    const chuong = chuongMap[idChuong || ''] || `chuong-${idChuong}`

    // Normalize bai to bai-{number} to match folder structure
    const baiMatch = (idBai || '').match(/(\d+)/)
    const baiNum = baiMatch ? parseInt(baiMatch[1], 10) : 1
    const bai = `bai-${baiNum}`

    return { lop, chuong, bai }
  }, [idLop, idChuong, idBai])

  // Mapping từ URL params sang đường dẫn thư mục
  const getLessonPath = () => {
    const lopMap: { [key: string]: string } = {
      '10': 'lop-10',
      '11': 'lop-11',
      '12': 'lop-12'
    }

    // Mapping chương cho từng lớp
    const chuongMapLop10: { [key: string]: string } = {
      '0': 'chuong-0-mo-dau',
      '1': 'chuong-1-dong-hoc',
      '2': 'chuong-2-dong-luc-hoc',
      '3': 'chuong-3-nang-luong-va-cong',
      '4': 'chuong-4-chuyen-dong-tron-va-bien-dang'
    }

    const chuongMapLop11: { [key: string]: string } = {
      '1': 'chuong-1-dao-dong',
      '2': 'chuong-2-song',
      '3': 'chuong-3-dien-truong',
      '4': 'chuong-4-dong-dien-mach-dien',
      '5': 'chuong-5-tu-truong'
    }

    const chuongMapLop12: { [key: string]: string } = {
      '1': 'chuong-1-vat-li-nhiet',
      '2': 'chuong-2-khi-li-tuong',
      '3': 'chuong-3-tu-truong',
      '4': 'chuong-4-vat-li-hat-nhan'
    }

    const lop = lopMap[idLop || ''] || `lop-${idLop}`

    // Chọn mapping chương theo lớp
    let chuongMap: { [key: string]: string } = {}
    if (idLop === '10') {
      chuongMap = chuongMapLop10
    } else if (idLop === '11') {
      chuongMap = chuongMapLop11
    } else if (idLop === '12') {
      chuongMap = chuongMapLop12
    }

    const chuong = chuongMap[idChuong || ''] || `chuong-${idChuong}`
    const bai = idBai // Giữ nguyên idBai vì đã có prefix 'bai-'

    return {
      noiDung: `/du-lieu/noi-dung/${lop}/${chuong}/${bai}/noi-dung.json`,
      quiz: `/du-lieu/noi-dung/${lop}/${chuong}/${bai}/quiz.json`
    }
  }

  useEffect(() => {
    loadLessonContent()
  }, [idLop, idChuong, idBai])

  useEffect(() => {
    if (currentSection !== 'lyThuyet') {
      setShowSummaryPanel(false)
    }
  }, [currentSection])

  useEffect(() => {
    setSummaryAcknowledged(false)
    setShowSummaryPanel(false)
  }, [lessonId])

  useEffect(() => {
    if (summaryAcknowledged || currentSection !== 'lyThuyet' || summaryPoints.length < 3 || isQuizFullscreen) {
      return
    }

    const triggerEl = summaryTriggerRef.current
    if (!triggerEl) {
      return
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setShowSummaryPanel(entry.isIntersecting)
      },
      {
        threshold: 0.15,
        rootMargin: '0px 0px -120px 0px'
      }
    )

    observer.observe(triggerEl)

    return () => {
      observer.disconnect()
    }
  }, [summaryAcknowledged, currentSection, summaryPoints.length, isQuizFullscreen])

  useEffect(() => {
    if (!summaryActive) {
      setActiveSummarySection(null)
      return
    }

    const sectionObservers: Array<{ key: string; ref: React.RefObject<HTMLDivElement> }> = [
      { key: 'phanMoDau', ref: moDauSectionRef },
      { key: 'phanNoiDung', ref: noiDungSectionRef },
      { key: 'phanCuoi', ref: phanCuoiSectionRef }
    ]

    const observer = new IntersectionObserver(
      (entries) => {
        const visibleEntry = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0]

        if (visibleEntry?.target instanceof HTMLElement) {
          const key = visibleEntry.target.getAttribute('data-summary-section')
          if (key) {
            setActiveSummarySection(key)
          }
        }
      },
      {
        threshold: 0.3,
        rootMargin: '-160px 0px -80px 0px'
      }
    )

    sectionObservers.forEach(({ ref, key }) => {
      if (ref.current) {
        ref.current.setAttribute('data-summary-section', key)
        observer.observe(ref.current)
      }
    })

    return () => {
      observer.disconnect()
    }
  }, [summaryActive])

  // Tạo link YouTube dựa trên thông tin bài học
  const generateYouTubeLink = (lop: string, chuong: string, bai: string, tieuDe: string) => {
    // Tạo ID video dựa trên thông tin bài học
    const baiNumber = bai.replace('bai-', '').split('-')[0]
    const chuongNumber = chuong.replace('chuong-', '').split('-')[0]

    // Tạo video ID duy nhất cho mỗi bài
    const videoId = `vatli365-lop${lop}-chuong${chuongNumber}-bai${baiNumber}`

    return {
      videoUrl: `https://www.youtube.com/watch?v=${videoId}`,
      youtubeUrl: `https://www.youtube.com/watch?v=${videoId}`,
      moTa: `Video giảng dạy chi tiết: ${tieuDe} - Lớp ${lop}, Chương ${chuongNumber}, Bài ${baiNumber}`
    }
  }

  const loadLessonContent = async () => {
    setLoading(true)
    setError(null)

    try {
      // Extract lesson parameters
      const lopNum = parseInt(idLop || '10')
      const chuongNum = parseInt((idChuong || '1').replace('chuong-', '').split('-')[0])
      const baiNum = parseInt((idBai || '1').replace('bai-', '').split('-')[0])
      const lessonId = `lop-${lopNum}-chuong-${chuongNum}-bai-${baiNum}`

      // ✅ DEBUG: Log teacherId ngay đầu function để đảm bảo không bị mất
      console.log('🔄 [LOAD START] Loading lesson:', {
        lop: lopNum,
        chuong: chuongNum,
        bai: baiNum,
        teacherId,
        teacherIdType: typeof teacherId,
        teacherIdExists: !!teacherId,
        urlSearch: window.location.search
      })

      // Reset teacher simulation content state
      setTeacherSimulationContent(null)
      setTeacherInfographicContent(null)

      // Nếu có teacherId trong query param, load teacher content
      let teacherTheoryContent: any = null
      let teacherVideoContent: any = null
      let teacherSimulationContent: any = null
      let teacherQuizContent: any = null
      let teacherInfographicContent: any = null

      if (teacherId) {
        try {
          console.log('👨‍🏫 Loading teacher content for:', teacherId, 'lesson:', lessonId)
          // Promise.race with timeout for teacher content
          const teacherContentPromise = teacherContentService.getAllContent(teacherId, lessonId, true)
          const timeoutPromise = new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Teacher content timeout')), 10000)
          )

          const allContent = await Promise.race([teacherContentPromise, timeoutPromise]) as any
          teacherTheoryContent = allContent.theory
          teacherVideoContent = allContent.video
          teacherSimulationContent = allContent.simulation
          teacherQuizContent = allContent.quiz
          teacherInfographicContent = allContent.infographic

          // Update state
          setTeacherSimulationContent(teacherSimulationContent)
          setTeacherInfographicContent(teacherInfographicContent)

          console.log('✅ Teacher content loaded:', {
            theory: !!teacherTheoryContent,
            video: !!teacherVideoContent,
            simulation: !!teacherSimulationContent,
            quiz: !!teacherQuizContent,
            infographic: !!teacherInfographicContent
          })
        } catch (e) {
          console.warn('⚠️ Failed to load teacher content:', e)
          // Fallback về localStorage
          try {
            const allContent = await teacherContentService.getAllContent(teacherId, lessonId, false)
            teacherTheoryContent = allContent.theory
            teacherVideoContent = allContent.video
            teacherSimulationContent = allContent.simulation
            teacherQuizContent = allContent.quiz
            teacherInfographicContent = allContent.infographic

            setTeacherSimulationContent(teacherSimulationContent)
            setTeacherInfographicContent(teacherInfographicContent)
          } catch (fallbackErr) {
            console.warn('⚠️ Failed to load from localStorage fallback:', fallbackErr)
          }
        }

        if (teacherTheoryContent) console.log('✅ Teacher theory content loaded')
        if (teacherVideoContent) console.log('✅ Teacher video content loaded')

        // Log Quiz Metadata
        if (teacherQuizContent) {
          console.log('✅ Teacher quiz metadata loaded:', {
            hasContentData: !!teacherQuizContent.contentData,
            source: teacherQuizContent.contentData?.source,
            type: teacherQuizContent.contentData?.type
          })
        } else {
          console.warn('⚠️ Teacher quiz content is NULL')
        }
      }

      // Use ContentService which automatically injects simulations
      const lessonFromService = await ContentServiceSimple.loadLesson(lopNum, chuongNum, baiNum)

      if (!lessonFromService) {
        throw new Error('Không thể load bài học từ ContentService')
      }

      console.log('✅ Lesson loaded from ContentService:', lessonFromService)

      // Convert ContentService format to XemBaiHoc format
      // Ưu tiên teacher content nếu có
      let phanMoDau = lessonFromService.cac_phan.find((p: any) => p.id === 'mo_dau')
      let phanNoiDung = lessonFromService.cac_phan.find((p: any) => p.id === 'noi_dung')
      let phanCuoi = lessonFromService.cac_phan.find((p: any) => p.id === 'cuoi')

      // Nếu có teacher theory content, sử dụng nó (ƯU TIÊN)
      if (teacherTheoryContent?.contentData) {
        console.log('👨‍🏫 Teacher theory content found:', {
          hasContentData: !!teacherTheoryContent.contentData,
          hasNoiDung: !!teacherTheoryContent.contentData.noiDung,
          structure: Object.keys(teacherTheoryContent.contentData)
        })

        const theoryData = teacherTheoryContent.contentData

        // Kiểm tra cấu trúc LessonData
        if (theoryData.noiDung) {
          console.log('✅ Using teacher theory content structure')

          // Sử dụng trực tiếp từ teacher content
          if (theoryData.noiDung.phanMoDau) {
            phanMoDau = {
              id: 'mo_dau',
              tieu_de: theoryData.noiDung.phanMoDau.tieuDe || 'Phần mở đầu',
              noi_dung: Array.isArray(theoryData.noiDung.phanMoDau.noiDung)
                ? theoryData.noiDung.phanMoDau.noiDung
                : [],
              thoi_gian_uoc_tinh: 0
            } as any
            console.log('📘 Teacher phanMoDau:', phanMoDau.noi_dung.length, 'items')
          }

          if (theoryData.noiDung.phanNoiDung) {
            phanNoiDung = {
              id: 'noi_dung',
              tieu_de: theoryData.noiDung.phanNoiDung.tieuDe || 'Phần nội dung',
              noi_dung: Array.isArray(theoryData.noiDung.phanNoiDung.noiDung)
                ? theoryData.noiDung.phanNoiDung.noiDung
                : [],
              thoi_gian_uoc_tinh: 0
            } as any
            console.log('📗 Teacher phanNoiDung:', phanNoiDung.noi_dung.length, 'items')
          }

          if (theoryData.noiDung.phanCuoi) {
            phanCuoi = {
              id: 'cuoi',
              tieu_de: theoryData.noiDung.phanCuoi.tieuDe || 'Phần cuối',
              noi_dung: Array.isArray(theoryData.noiDung.phanCuoi.noiDung)
                ? theoryData.noiDung.phanCuoi.noiDung
                : [],
              thoi_gian_uoc_tinh: 0
            } as any
            console.log('📙 Teacher phanCuoi:', phanCuoi.noi_dung.length, 'items')
          }
        } else {
          console.warn('⚠️ Teacher theory content structure not recognized:', theoryData)
        }
      } else {
        console.log('ℹ️ No teacher theory content, using system content')
      }

      const noiDungData: LessonData = {
        id: typeof lessonFromService.id === 'string' ? parseInt(lessonFromService.id.split('-')[2]) : lessonFromService.id,
        tieuDe: teacherTheoryContent?.contentData?.tieuDe || lessonFromService.tieu_de,
        moTa: teacherTheoryContent?.contentData?.moTa || lessonFromService.muc_tieu?.[0] || '',
        metadata: {
          thoiGian: lessonFromService.thoi_luong || 45,
          doKho: lessonFromService.do_kho || 'trung bình',
          chuDe: lessonFromService.the || [],
          mucTieu: lessonFromService.muc_tieu || []
        },
        noiDung: {
          phanMoDau: {
            tieuDe: phanMoDau?.tieu_de || 'Phần mở đầu',
            noiDung: Array.isArray(phanMoDau?.noi_dung) ? phanMoDau.noi_dung : []
          },
          phanNoiDung: {
            tieuDe: phanNoiDung?.tieu_de || 'Phần nội dung',
            noiDung: Array.isArray(phanNoiDung?.noi_dung) ? phanNoiDung.noi_dung : []
          },
          phanCuoi: {
            tieuDe: phanCuoi?.tieu_de || 'Phần cuối',
            noiDung: Array.isArray(phanCuoi?.noi_dung) ? phanCuoi.noi_dung : []
          }
        }
      }

      console.log('✅ Converted lesson data with sections:', {
        phanMoDau: noiDungData.noiDung.phanMoDau.noiDung.length,
        phanNoiDung: noiDungData.noiDung.phanNoiDung.noiDung.length,
        phanCuoi: noiDungData.noiDung.phanCuoi.noiDung.length,
        isTeacherContent: !!teacherTheoryContent?.contentData
      })

      // Set lessonData - đảm bảo teacher content được ưu tiên
      setLessonData(noiDungData)
      console.log('📚 lessonData set with teacher content:', {
        hasTeacherContent: !!teacherTheoryContent?.contentData,
        phanMoDauItems: noiDungData.noiDung.phanMoDau.noiDung.length,
        phanNoiDungItems: noiDungData.noiDung.phanNoiDung.noiDung.length,
        phanCuoiItems: noiDungData.noiDung.phanCuoi.noiDung.length
      })

      // Load quiz từ nội dung (legacy)
      let quizData = null
      const paths = getLessonPath()
      const isDevelopment = import.meta.env.DEV
      const cacheBuster = isDevelopment ? `?t=${Date.now()}` : ''

      try {
        const quizResponse = await fetch(paths.quiz + cacheBuster, {
          cache: isDevelopment ? 'no-cache' : 'default'
        })
        if (quizResponse.ok) {
          quizData = await quizResponse.json()
          console.log('✅ Quiz loaded:', quizData)
          setQuizData(quizData)
        }
      } catch (quizError) {
        console.warn('⚠️ Quiz not available:', quizError)
      }

      // Load câu hỏi từ bank (hỗ trợ teacher database)
      // ✅ DEBUG: Log teacherId ngay đầu để đảm bảo không bị mất
      console.log('🎯 [QUIZ LOAD START] Teacher ID check:', {
        teacherId,
        teacherIdType: typeof teacherId,
        teacherIdExists: !!teacherId,
        hasTeacherQuizContent: !!teacherQuizContent,
        urlParams: window.location.search
      })

      try {
        // ✅ FIX: Extract số từ idLop, idChuong, idBai để đảm bảo format đúng
        // idLop có thể là "11" hoặc "lop-11"
        let lopParam = idLop || '10'
        const lopMatch = lopParam.match(/(\d+)/)
        if (lopMatch) {
          lopParam = lopMatch[1]
        }

        // idChuong có thể là "1" hoặc "chuong-1"
        let chuongParam = idChuong || '1'
        const chuongMatch = chuongParam.match(/(\d+)/)
        if (chuongMatch) {
          chuongParam = chuongMatch[1]
        }

        // idBai có thể là "1", "bai-1", hoặc "1-dao-dong-dieu-hoa"
        let baiParam = idBai?.replace('bai-', '') || '1'
        const baiNumberMatch = baiParam.match(/^(\d+)/)
        if (baiNumberMatch) {
          baiParam = baiNumberMatch[1]
        }

        console.log('🎯 Bank service parameters (EXTRACTED):', {
          originalIdLop: idLop,
          originalIdChuong: idChuong,
          originalIdBai: idBai,
          extractedLop: lopParam,
          extractedChuong: chuongParam,
          extractedBai: baiParam,
          teacherId,
          teacherIdStillExists: !!teacherId
        })

        // ✅ FIX: Nếu có teacherId, ƯU TIÊN load từ teacher database
        // Logic: Nếu có teacherId → thử load từ teacher databases trước
        // Nếu không có hoặc load thất bại → fallback về system
        let bankQuiz

        // ✅ DEBUG: Log teacherId explicitly
        console.log('🔍 [CRITICAL] Teacher ID check:', {
          teacherIdFromURL: teacherId,
          teacherIdType: typeof teacherId,
          teacherIdExists: !!teacherId,
          teacherIdValue: teacherId || 'NULL/UNDEFINED'
        })

        // ✅ DEBUG: Log chi tiết về teacherQuizContent
        console.log('🔍 [QUIZ DEBUG] Checking teacher quiz content:', {
          hasTeacherId: !!teacherId,
          hasTeacherQuizContent: !!teacherQuizContent,
          teacherQuizContentType: typeof teacherQuizContent,
          teacherQuizContentKeys: teacherQuizContent ? Object.keys(teacherQuizContent) : [],
          hasContentData: !!teacherQuizContent?.contentData,
          contentDataType: typeof teacherQuizContent?.contentData,
          contentDataKeys: teacherQuizContent?.contentData ? Object.keys(teacherQuizContent.contentData) : [],
          quizSource: teacherQuizContent?.contentData?.source,
          quizType: teacherQuizContent?.contentData?.type,
          fullContentData: teacherQuizContent?.contentData
        })

        // ✅ FIX: ALWAYS try teacher databases if teacherId exists
        const shouldTryTeacherQuiz = !!teacherId
        console.log('🔍 Quiz loading decision:', {
          hasTeacherId: !!teacherId,
          hasTeacherQuizContent: !!teacherQuizContent,
          quizSource: teacherQuizContent?.contentData?.source,
          shouldTryTeacherQuiz,
          willLoadFromTeacher: shouldTryTeacherQuiz,
          FORCE_TEACHER_DB: '✅ YES if teacherId exists'
        })

        if (shouldTryTeacherQuiz) {
          console.log('👨‍🏫 Teacher ID found, attempting to load quiz from teacher database...')
          console.log('📋 Quiz loading params:', { teacherId, lop: lopParam, chuong: chuongParam, bai: baiParam })

          try {
            // ✅ FIX: Sử dụng preloaded databases nếu có, nếu không thì load mới
            let tnDb: Uint8Array | null = null
            let dsDb: Uint8Array | null = null
            let tlDb: Uint8Array | null = null

            if (teacherDatabasesPreloaded) {
              console.log('✅ [Phần 4 - Database] Using preloaded databases')
              tnDb = teacherDatabasesPreloaded.TN
              dsDb = teacherDatabasesPreloaded.DS
              tlDb = teacherDatabasesPreloaded.TL
            } else {
              console.log('⚠️ [Phần 4 - Database] No preloaded databases, loading now...')

              // ✅ FIX: Check database availability trước (sử dụng cache)
              const availability = await databaseAvailabilityService.checkDatabaseAvailability(teacherId, lopParam, chuongParam, baiParam)
              console.log('📊 [Phần 4 - Database] Database availability:', availability)

              // Load databases từ server first (chỉ nếu có database từ server)
              if (availability.hasServerDatabase) {
                try {
                  const serverDbs = await teacherContentService.getTeacherDatabases(teacherId, lopParam, chuongParam, baiParam, 3000)
                  tnDb = serverDbs.TN
                  dsDb = serverDbs.DS
                  tlDb = serverDbs.TL
                } catch (e) {
                  console.warn('⚠️ Failed to fetch databases from server:', e)
                }
              } else {
                console.log('⚠️ [Phần 4 - Database] No server database available (from cache), skipping server fetch')
              }

              // If not found on server, try local IndexedDB (chỉ nếu có database trong IndexedDB)
              if ((!tnDb || tnDb.length === 0) && (!dsDb || dsDb.length === 0) && (!tlDb || tlDb.length === 0)) {
                if (availability.hasIndexedDB) {
                  console.log('⚠️ No server databases found, checking local IndexedDB...')
                } else {
                  console.log('⚠️ [Phần 4 - Database] No server databases and no IndexedDB (from cache), skipping IndexedDB check')
                }
                try {
                  const indexedDBPromise = Promise.all([
                    teacherContentService.loadDatabaseFromIndexedDB(teacherId, lopParam, chuongParam, baiParam, 'TN').catch(() => null),
                    teacherContentService.loadDatabaseFromIndexedDB(teacherId, lopParam, chuongParam, baiParam, 'DS').catch(() => null),
                    teacherContentService.loadDatabaseFromIndexedDB(teacherId, lopParam, chuongParam, baiParam, 'TL').catch(() => null)
                  ])
                  const timeoutPromise = new Promise((_, reject) =>
                    setTimeout(() => reject(new Error('IndexedDB timeout')), 2000)
                  )
                  const localDbs = await Promise.race([indexedDBPromise, timeoutPromise]) as any[]
                  tnDb = localDbs[0]
                  dsDb = localDbs[1]
                  tlDb = localDbs[2]
                } catch (e) {
                  console.warn('⚠️ Failed to load from IndexedDB:', e)
                }
              }
            }

            const hasTeacherData = (tnDb && tnDb.length > 0) || (dsDb && dsDb.length > 0) || (tlDb && tlDb.length > 0)

            console.log(`🔍 [Phần 4 - Database] Has teacher data: ${hasTeacherData}`, {
              TN: tnDb ? `${tnDb.length} bytes` : 'null',
              DS: dsDb ? `${dsDb.length} bytes` : 'null',
              TL: tlDb ? `${tlDb.length} bytes` : 'null',
              fromPreload: !!teacherDatabasesPreloaded
            })

            // ✅ FIX: Chỉ load từ teacher database nếu có dữ liệu
            if (hasTeacherData) {
              console.log('✅ [Phần 4 - Database] Teacher databases found, loading quiz from teacher data...')
              bankQuiz = await bankQuestionService.getRandomQuestions(
                lopParam,
                chuongParam,
                baiParam,
                {
                  thoiGianLamBai: 15,
                  diemDat: 70
                },
                teacherId,
                { TN: tnDb, DS: dsDb, TL: tlDb }
              )
              console.log('✅ [Phần 4 - Database] Teacher quiz loaded successfully:', {
                questionCount: bankQuiz?.cauHoi?.length || 0,
                hasQuestions: !!(bankQuiz?.cauHoi && bankQuiz.cauHoi.length > 0),
                title: bankQuiz?.tieuDe
              })
            } else {
              // ✅ FIX: Fallback nhanh về system nếu không có teacher database
              console.warn('⚠️ [Phần 4 - Database] No teacher databases found, falling back to system quiz')
              bankQuiz = await bankQuestionService.getRandomQuestions(
                lopParam,
                chuongParam,
                baiParam,
                {
                  thoiGianLamBai: 15,
                  diemDat: 70
                }
              )
              console.log('✅ [Phần 4 - Database] System quiz loaded (no teacher data):', bankQuiz?.cauHoi?.length || 0, 'questions')
            }
          } catch (teacherQuizError) {
            console.error('❌ [Phần 4 - Database] Error loading teacher quiz:', teacherQuizError)
            console.warn('⚠️ [Phần 4 - Database] Falling back to system quiz due to error')
            // ✅ FIX: Fallback nhanh về system khi có lỗi
            bankQuiz = await bankQuestionService.getRandomQuestions(
              lopParam,
              chuongParam,
              baiParam,
              {
                thoiGianLamBai: 15,
                diemDat: 70
              }
            )
            console.log('✅ [Phần 4 - Database] System quiz loaded (error fallback):', bankQuiz?.cauHoi?.length || 0, 'questions')
          }
        } else {
          // Load từ system (không có teacherId)
          console.log('ℹ️ No teacher ID, loading system quiz...')
          bankQuiz = await bankQuestionService.getRandomQuestions(
            lopParam,
            chuongParam,
            baiParam,
            {
              thoiGianLamBai: 15,
              diemDat: 70
            }
          )
          console.log('✅ System quiz loaded:', bankQuiz?.cauHoi?.length || 0, 'questions')
        }

        setBankQuizData(bankQuiz)
      } catch (bankError) {
        console.warn('⚠️ Bank questions not available:', bankError)
      }

      // Tạo dữ liệu cho các phần của bài học
      // Lọc nội dung lý thuyết, loại bỏ các phần bài tập
      const filterTheoryContent = (content: any[]) => {
        return content.filter(item => {
          // Loại bỏ các item có type là quiz, exercise hoặc có title chứa từ khóa bài tập
          if (item.type === 'quiz' || item.type === 'exercise') {
            return false
          }
          if (item.title && (
            item.title.toLowerCase().includes('bài tập') ||
            item.title.toLowerCase().includes('câu hỏi') ||
            item.title.toLowerCase().includes('trắc nghiệm') ||
            item.title.toLowerCase().includes('kiểm tra')
          )) {
            return false
          }
          return true
        })
      }

      // Sử dụng YouTube URL thực từ dữ liệu bài học hoặc mapping table, nếu không có thì tạo link tự động
      const lopParam = idLop || '10'
      const chuongParam = idChuong || '1'

      // Trích xuất số bài từ idBai (có thể có format: bai-1-dao-dong-dieu-hoa...)
      let baiParam = idBai?.replace('bai-', '') || '1'
      // Lấy chỉ số đầu tiên từ chuỗi (ví dụ: "1-dao-dong-dieu-hoa" -> "1")
      const baiNumberMatch = baiParam.match(/^(\d+)/)
      if (baiNumberMatch) {
        baiParam = baiNumberMatch[1]
      }

      console.log('🔍 YouTube URL Debug:', {
        lopParam,
        chuongParam,
        baiParam,
        originalBai: idBai,
        mappingKey: `${lopParam}-${chuongParam}-${baiParam}`,
        hasYoutubeUrl: !!noiDungData.youtubeUrl
      })

      const actualYoutubeUrl = noiDungData.youtubeUrl || getYouTubeUrl(lopParam, chuongParam, baiParam)
      console.log('🎥 YouTube URL Result:', actualYoutubeUrl)

      // Kiểm tra nếu URL có chứa placeholder
      const isValidUrl = actualYoutubeUrl && !actualYoutubeUrl.includes('YOUR_VIDEO_ID')
      console.log('✅ URL Validation:', { actualYoutubeUrl, isValidUrl, willUseFallback: !isValidUrl })

      const youtubeLinks = isValidUrl ? {
        videoUrl: actualYoutubeUrl,
        youtubeUrl: actualYoutubeUrl,
        moTa: `Video giảng dạy chi tiết: ${noiDungData.tieuDe}`
      } : generateYouTubeLink(lopParam, chuongParam, idBai || 'bai-1', noiDungData.tieuDe)

      // Sử dụng teacher video nếu có
      let finalVideoUrl = youtubeLinks.youtubeUrl
      let finalVideoMoTa = youtubeLinks.moTa

      // ✅ FIX: Check structure thực tế - video được lưu dạng { type, url, title, description, source }
      if (teacherVideoContent?.contentData) {
        const videoData = teacherVideoContent.contentData
        // Support cả 2 cấu trúc: object trực tiếp hoặc items array
        const teacherVideo = videoData.url ? videoData : (videoData.items?.[0] || null)
        if (teacherVideo) {
          const videoUrl = teacherVideo.url || teacherVideo.content || teacherVideo.src
          if (videoUrl) {
            finalVideoUrl = videoUrl
            finalVideoMoTa = teacherVideo.description || teacherVideo.title || videoData.title || 'Video từ giáo viên'
            console.log('✅ Using teacher video:', { url: finalVideoUrl, title: finalVideoMoTa })
          }
        }
      } else if (teacherTheoryContent?.contentData?.noiDung) {
        // Fallback: Extract video from theory content
        const allItems = [
          ...(teacherTheoryContent.contentData.noiDung.phanMoDau?.noiDung || []),
          ...(teacherTheoryContent.contentData.noiDung.phanNoiDung?.noiDung || []),
          ...(teacherTheoryContent.contentData.noiDung.phanCuoi?.noiDung || [])
        ]
        const videoItem = allItems.find((item: any) => item.type === 'video' || item.type === 'youtube')
        if (videoItem) {
          finalVideoUrl = videoItem.content || videoItem.src
          finalVideoMoTa = videoItem.caption || 'Video từ bài học'
          console.log('🎥 Extracted video from theory content:', finalVideoUrl)
        }
      }

      // Sử dụng teacher simulation nếu có
      let finalSimulationUrl = ''
      let finalSimulationMoTa = 'Xem video mô phỏng để hiểu rõ hơn về bài học'

      // ✅ FIX: Check structure thực tế - simulation được lưu dạng { type, url, title, description, source }
      if (teacherSimulationContent?.contentData) {
        const simData = teacherSimulationContent.contentData
        // Support cả 2 cấu trúc: object trực tiếp hoặc items array
        const teacherSimulation = simData.url ? simData : (simData.items?.[0] || null)
        if (teacherSimulation) {
          let simUrl = teacherSimulation.url || teacherSimulation.content || teacherSimulation.src

          // ✅ FIX: Nếu path thiếu L{lop}/C{chuong}/B{bai}, thêm vào
          if (simUrl && !simUrl.startsWith('http') && !simUrl.startsWith('data:') && !simUrl.startsWith('blob:')) {
            if (simUrl.startsWith('/teacher-content/') || simUrl.startsWith('teacher-content/')) {
              const cleanUrl = simUrl.startsWith('/') ? simUrl : `/${simUrl}`
              const hasStructuredPath = /\/L\d+\/C\d+\/B\d+\//.test(cleanUrl)

              if (!hasStructuredPath) {
                // Extract teacherId và filename
                const teacherIdMatch = cleanUrl.match(/\/teacher-content\/([^\/]+)/)
                const pathParts = cleanUrl.split('/').filter(p => p)
                const filename = pathParts.pop() || ''

                if (teacherIdMatch && idLop && idChuong && idBai) {
                  const teacherId = teacherIdMatch[1]
                  const lopNum = parseInt(idLop) || 11
                  const chuongNum = parseInt(idChuong.replace('chuong-', '').split('-')[0]) || 1
                  const baiNum = parseInt(idBai.replace('bai-', '').split('-')[0]) || 1
                  const baiPadded = baiNum.toString().padStart(2, '0')

                  simUrl = `/teacher-content/${teacherId}/L${lopNum}/C${chuongNum}/B${baiPadded}/${filename}`
                  console.log(`🔧 [Simulation Path Fix] Thêm L/C/B: ${simUrl}`)
                }
              }
            }
          }

          if (simUrl) {
            finalSimulationUrl = simUrl
            finalSimulationMoTa = teacherSimulation.description || teacherSimulation.title || simData.title || 'Mô phỏng từ giáo viên'
            console.log('✅ Using teacher simulation:', { url: finalSimulationUrl, title: finalSimulationMoTa })
          }
        }
      } else if (teacherTheoryContent?.contentData?.noiDung) {
        // Fallback: Extract simulation from theory content
        const allItems = [
          ...(teacherTheoryContent.contentData.noiDung.phanMoDau?.noiDung || []),
          ...(teacherTheoryContent.contentData.noiDung.phanNoiDung?.noiDung || []),
          ...(teacherTheoryContent.contentData.noiDung.phanCuoi?.noiDung || [])
        ]
        const simItem = allItems.find((item: any) =>
          item.type === 'simulation' ||
          item.type === 'video_simulation' ||
          item.type === 'interactive_vt_graph' ||
          item.type === 'interactive_oscillation'
        )
        if (simItem) {
          let simUrl = simItem.content || simItem.src || simItem.url

          // ✅ FIX: Nếu path thiếu L{lop}/C{chuong}/B{bai}, thêm vào
          if (simUrl && !simUrl.startsWith('http') && !simUrl.startsWith('data:') && !simUrl.startsWith('blob:')) {
            if (simUrl.startsWith('/teacher-content/') || simUrl.startsWith('teacher-content/')) {
              const cleanUrl = simUrl.startsWith('/') ? simUrl : `/${simUrl}`
              const hasStructuredPath = /\/L\d+\/C\d+\/B\d+\//.test(cleanUrl)

              if (!hasStructuredPath) {
                // Extract teacherId và filename
                const teacherIdMatch = cleanUrl.match(/\/teacher-content\/([^\/]+)/)
                const pathParts = cleanUrl.split('/').filter(p => p)
                const filename = pathParts.pop() || ''

                if (teacherIdMatch && idLop && idChuong && idBai) {
                  const teacherId = teacherIdMatch[1]
                  const lopNum = parseInt(idLop) || 11
                  const chuongNum = parseInt(idChuong.replace('chuong-', '').split('-')[0]) || 1
                  const baiNum = parseInt(idBai.replace('bai-', '').split('-')[0]) || 1
                  const baiPadded = baiNum.toString().padStart(2, '0')

                  simUrl = `/teacher-content/${teacherId}/L${lopNum}/C${chuongNum}/B${baiPadded}/${filename}`
                  console.log(`🔧 [Simulation Path Fix from Theory] Thêm L/C/B: ${simUrl}`)
                }
              }
            }
          }

          finalSimulationUrl = simUrl
          finalSimulationMoTa = simItem.caption || simItem.description || 'Mô phỏng từ bài học'
          console.log('🎮 Extracted simulation from theory content:', finalSimulationUrl)
        }
      }

      // ✅ FIX: Không cần check source='custom' nữa, vì đã load quiz từ teacher databases ở trên
      // Nếu có teacherId và có bankQuizData, đó là teacher quiz
      const useTeacherQuiz = !!teacherId && !!bankQuizData

      // Tạo nội dung lý thuyết - ƯU TIÊN teacher content
      let lyThuyetNoiDung: any[] = []
      if (teacherTheoryContent?.contentData?.noiDung) {
        // Sử dụng trực tiếp từ teacher content (KHÔNG filter vì đã là teacher content)
        console.log('👨‍🏫 Using teacher theory content for baiHocSections')
        const theoryData = teacherTheoryContent.contentData
        if (theoryData.noiDung.phanMoDau?.noiDung) {
          lyThuyetNoiDung.push(...(Array.isArray(theoryData.noiDung.phanMoDau.noiDung) ? theoryData.noiDung.phanMoDau.noiDung : []))
        }
        if (theoryData.noiDung.phanNoiDung?.noiDung) {
          lyThuyetNoiDung.push(...(Array.isArray(theoryData.noiDung.phanNoiDung.noiDung) ? theoryData.noiDung.phanNoiDung.noiDung : []))
        }
        if (theoryData.noiDung.phanCuoi?.noiDung) {
          lyThuyetNoiDung.push(...(Array.isArray(theoryData.noiDung.phanCuoi.noiDung) ? theoryData.noiDung.phanCuoi.noiDung : []))
        }
        console.log('✅ Teacher theory content items:', lyThuyetNoiDung.length)
      } else {
        // Fallback to system content
        console.log('ℹ️ Using system theory content for baiHocSections')
        lyThuyetNoiDung = [
          ...filterTheoryContent(noiDungData.noiDung?.phanMoDau?.noiDung || []),
          ...filterTheoryContent(noiDungData.noiDung?.phanNoiDung?.noiDung || []),
          ...filterTheoryContent(noiDungData.noiDung?.phanCuoi?.noiDung || [])
        ]
      }

      const mockBaiHocSections: BaiHocSections = {
        lyThuyet: {
          noiDung: lyThuyetNoiDung,
          videoUrl: finalVideoUrl
        },
        video: {
          youtubeUrl: finalVideoUrl,
          moTa: finalVideoMoTa
        },
        tracNghiem: {
          // ƯU TIÊN bankQuizData (có thể từ teacher hoặc system)
          // Nếu có teacherId và bankQuizData, đó là teacher quiz
          cauHoi: bankQuizData?.cauHoi || quizData?.cauHoi || [],
          thoiGian: bankQuizData?.gioi_han_thoi_gian ? Math.floor(bankQuizData.gioi_han_thoi_gian / 60) : (quizData?.metadata?.thoiGian || 15),
          metadata: bankQuizData ? {
            thoiGian: Math.floor(bankQuizData.gioi_han_thoi_gian / 60),
            soCauHoi: bankQuizData.cauHoi.length,
            diemDat: bankQuizData.diem_dat,
            doKho: 'trung bình',
            chuDe: [`Lớp ${idLop}`, `Chương ${idChuong}`, `Bài ${idBai}`],
            isTeacherQuiz: !!teacherId // Đánh dấu là teacher quiz
          } : (quizData?.metadata || {})
        },
        moPhong: {
          ten: 'Mô phỏng tương tác',
          moTa: finalSimulationMoTa,
          url: finalSimulationUrl || '' // Will load component dynamically if empty
        },
        infographic: {
          url: teacherInfographicContent?.contentData?.url || '',
          description: teacherInfographicContent?.contentData?.description || 'Infographic tóm tắt bài học'
        }
      }

      // Load simulation component dynamically - use idBai directly (already has full name)
      loadSimulationComponent(idLop || '10', idChuong || '1', idBai || 'bai-1')

      // Kiểm tra nếu không có nội dung lý thuyết
      if (mockBaiHocSections.lyThuyet.noiDung.length === 0) {
        console.warn('⚠️ Không có nội dung lý thuyết cho bài học này')
        mockBaiHocSections.lyThuyet.noiDung = [
          {
            type: 'text',
            content: 'Nội dung bài học đang được cập nhật. Vui lòng quay lại sau.'
          }
        ]
      }

      setBaiHocSections(mockBaiHocSections)
      toast.success('Nội dung bài học đã được tải thành công!')
    } catch (err) {
      console.error('❌ Error loading lesson:', err)
      const errorMessage = err instanceof Error ? err.message : 'Lỗi không xác định'
      setError(errorMessage)

      // Hiển thị thông tin chi tiết về lỗi
      if (err instanceof Error && err.message.includes('HTTP')) {
        toast.error('Không thể tải nội dung bài học. Vui lòng kiểm tra đường dẫn.')
      } else {
        toast.error('Không thể tải nội dung bài học')
      }

      // Tạo dữ liệu mặc định để tránh crash UI
      const fallbackYoutubeLinks = generateYouTubeLink(idLop || '10', idChuong || '1', idBai || 'bai-1', 'Bài học Vật lí')

      const fallbackBaiHocSections: BaiHocSections = {
        lyThuyet: {
          noiDung: [
            {
              type: 'text',
              content: 'Không thể tải nội dung bài học. Vui lòng thử lại sau.'
            }
          ],
          videoUrl: fallbackYoutubeLinks.videoUrl
        },
        video: {
          youtubeUrl: fallbackYoutubeLinks.youtubeUrl,
          moTa: fallbackYoutubeLinks.moTa
        },
        tracNghiem: {
          cauHoi: [],
          thoiGian: 0,
          metadata: {}
        },
        moPhong: {
          ten: 'Mô phỏng không khả dụng',
          moTa: 'Mô phỏng không thể tải',
          url: '#'
        }
      }

      setBaiHocSections(fallbackBaiHocSections)
    } finally {
      setLoading(false)
    }
  }

  const handleBookmark = () => {
    setBookmarked(!bookmarked)
    toast.success(bookmarked ? 'Đã bỏ đánh dấu bài học' : 'Đã đánh dấu bài học')
  }

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: lessonData?.tieuDe || 'Bài học AI-PhysicsBook',
        url: window.location.href
      })
    } else {
      navigator.clipboard.writeText(window.location.href)
      toast.success('Đã sao chép liên kết vào clipboard')
    }
  }

  // Load simulation component dynamically - using video from src/videos
  const loadSimulationComponent = async (lopParam: string, chuongParam: string, baiParam: string) => {
    setSimulationLoading(true)
    setSimulationError(null)

    try {
      console.log('🎮 Loading simulation video:', { lopParam, chuongParam, baiParam })

      // Import VideoSimulation component
      const { default: VideoSimulation } = await import('../../components/interactive/shared/simulations/VideoSimulation')

      // Set the video simulation component
      setSimulationComponent(() => VideoSimulation)

    } catch (error) {
      console.error('❌ Failed to load simulation video:', error)
      setSimulationError(error instanceof Error ? error.message : 'Không thể tải video mô phỏng')
      setSimulationComponent(null)
    } finally {
      setSimulationLoading(false)
    }
  }

  // Generate video path from lesson parameters - uses VideoService
  const generateVideoPath = (lop: string, chuong: string, bai: string) => {
    // Sử dụng VideoService để tạo đường dẫn video thống nhất
    return VideoService.generateVideoPath(lop, chuong, bai)
  }

  const getSectionTitle = (section: string) => {
    switch (section) {
      case 'lyThuyet': return 'Lý thuyết'
      case 'video': return 'Video YouTube'
      case 'tracNghiem': return 'Bài tập trắc nghiệm'
      case 'moPhong': return 'Mô phỏng'
      case 'infographic': return 'Infographic AI'
      default: return 'Nội dung'
    }
  }

  const getSectionIcon = (section: string) => {
    switch (section) {
      case 'lyThuyet': return '📚'
      case 'video': return '🎥'
      case 'tracNghiem': return '🎯'
      case 'moPhong': return '🎮'
      case 'infographic': return '✨'
      default: return '📖'
    }
  }

  const getSectionContent = (section: string) => {
    if (!baiHocSections) return null

    switch (section) {
      case 'lyThuyet': return baiHocSections.lyThuyet
      case 'video': return baiHocSections.video
      case 'tracNghiem': return baiHocSections.tracNghiem
      case 'moPhong': return baiHocSections.moPhong
      case 'infographic': return baiHocSections.infographic
      default: return null
    }
  }

  const handleContentError = (error: Error, item: any) => {
    // Chỉ log warning, không thêm vào contentErrors nữa vì hầu hết là warnings không nghiêm trọng
    console.warn('Content render error:', error, item)

    // Chỉ thêm vào contentErrors nếu là lỗi nghiêm trọng (ảnh hưởng đến việc hiển thị nội dung)
    const isCriticalError = error.message.includes('Cannot render') ||
      error.message.includes('Failed to load') ||
      error.message.includes('Invalid content structure')

    if (isCriticalError) {
      setContentErrors(prev => [...prev, { error, item, severity: 'critical' }])
    }
  }

  const nextSection = () => {
    const sections: Array<'lyThuyet' | 'video' | 'tracNghiem' | 'moPhong' | 'infographic'> = ['lyThuyet', 'video', 'infographic', 'moPhong', 'tracNghiem']
    const currentIndex = sections.indexOf(currentSection as any)
    if (currentIndex < sections.length - 1) {
      // Email sẽ được gửi tự động bởi useEffect theo dõi currentSection
      setCurrentSection(sections[currentIndex + 1])
    }
  }

  const prevSection = () => {
    const sections: Array<'lyThuyet' | 'video' | 'tracNghiem' | 'moPhong' | 'infographic'> = ['lyThuyet', 'video', 'infographic', 'moPhong', 'tracNghiem']
    const currentIndex = sections.indexOf(currentSection as any)
    if (currentIndex > 0) {
      setCurrentSection(sections[currentIndex - 1])
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        <Navigation />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <LoadingSpinner text="Đang tải nội dung bài học..." />
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        <Navigation />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-6">
              <AlertTriangle className="h-8 w-8 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold text-blue-700 mb-4">
              Không thể tải nội dung bài học
            </h1>
            <p className="text-gray-600 mb-6">
              {error}
            </p>
            <div className="space-y-3">
              <button
                onClick={loadLessonContent}
                className="btn-primary inline-flex items-center px-6 py-3 font-medium rounded-lg"
              >
                🔄 Thử lại
              </button>
              <button
                onClick={() => navigate('/')}
                className="block mx-auto mt-4 text-primary-600 hover:text-primary-700 font-medium"
              >
                ← Về trang chủ
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!lessonData || !baiHocSections) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        <Navigation />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <p className="text-gray-600">Không có dữ liệu bài học</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Navigation hidden for focused learning experience */}

      {!isQuizFullscreen && (
        <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-6 py-2 sm:py-4">
          {/* Breadcrumb - Compact */}
          <nav className="flex items-center space-x-1 text-[0.7rem] sm:text-xs text-gray-600 mb-1 sm:mb-2 overflow-x-auto pb-1">
            <Link to="/" className="hover:text-primary-600 transition-colors whitespace-nowrap">
              Trang chủ
            </Link>
            <ChevronRight className="w-2.5 h-2.5 sm:w-3 sm:h-3 shrink-0" />
            <Link to={`/lop/${idLop}`} className="hover:text-primary-600 transition-colors whitespace-nowrap">
              Lớp {idLop}
            </Link>
            <ChevronRight className="w-2.5 h-2.5 sm:w-3 sm:h-3 shrink-0" />
            <Link to={`/lop/${idLop}/chuong/${idChuong}`} className="hover:text-primary-600 transition-colors whitespace-nowrap">
              Chương {idChuong}
            </Link>
            <ChevronRight className="w-2.5 h-2.5 sm:w-3 sm:h-3 shrink-0" />
            <span className="text-blue-700 font-medium whitespace-nowrap">Bài {idBai}</span>
          </nav>

          {/* Lesson Header - Compact */}
          <div className="bg-white rounded-lg sm:rounded-xl shadow-md p-2 sm:p-4 mb-2 sm:mb-4">
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between">
              <div className="flex-1">
                <h1 className="text-base sm:text-2xl font-bold text-blue-700 mb-1 sm:mb-2 leading-tight">
                  {lessonData.tieuDe}
                </h1>
                <p className="text-xs sm:text-sm text-gray-600 mb-2 sm:mb-3 leading-tight">
                  {lessonData.moTa}
                </p>

                {/* Metadata - Compact */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-1.5 sm:gap-3 mb-2 sm:mb-3">
                  <div className="flex items-center space-x-1 sm:space-x-2 p-1.5 sm:p-2 bg-primary-50 rounded">
                    <Clock className="w-3 h-3 sm:w-4 sm:h-4 text-primary-600 shrink-0" />
                    <div>
                      <p className="text-[0.65rem] sm:text-xs text-gray-600 leading-tight">Thời gian</p>
                      <p className="font-semibold text-xs sm:text-sm text-blue-700 leading-tight">{lessonData.metadata.thoiGian}p</p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-1 sm:space-x-2 p-1.5 sm:p-2 bg-green-50 rounded">
                    <Target className="w-3 h-3 sm:w-4 sm:h-4 text-green-600 shrink-0" />
                    <div>
                      <p className="text-[0.65rem] sm:text-xs text-gray-600 leading-tight">Độ khó</p>
                      <p className="font-semibold text-xs sm:text-sm text-blue-700 capitalize leading-tight">{lessonData.metadata.doKho}</p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-1 sm:space-x-2 p-1.5 sm:p-2 bg-blue-50 rounded">
                    <Brain className="w-3 h-3 sm:w-4 sm:h-4 text-blue-600 shrink-0" />
                    <div>
                      <p className="text-[0.65rem] sm:text-xs text-gray-600 leading-tight">Chủ đề</p>
                      <p className="font-semibold text-xs sm:text-sm text-blue-700 leading-tight">{lessonData.metadata.chuDe.length}</p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-1 sm:space-x-2 p-1.5 sm:p-2 bg-purple-50 rounded">
                    <BookOpen className="w-3 h-3 sm:w-4 sm:h-4 text-purple-600 shrink-0" />
                    <div>
                      <p className="text-[0.65rem] sm:text-xs text-gray-600 leading-tight">Mục tiêu</p>
                      <p className="font-semibold text-xs sm:text-sm text-blue-700 leading-tight">{lessonData.metadata.mucTieu.length}</p>
                    </div>
                  </div>
                </div>

                {/* Learning Objectives - Hidden to save space, user can see by clicking */}
              </div>

              {/* Action Buttons - Minimal Icons Only */}
              <div className="flex flex-row items-center space-x-1 sm:space-x-1 mt-2 lg:mt-0 lg:ml-3">
                <button
                  onClick={handleBookmark}
                  className={`flex items-center justify-center p-0.5 sm:p-1.5 sm:rounded sm:transition-colors ${bookmarked
                    ? 'sm:bg-blue-600 sm:text-white text-blue-600'
                    : 'sm:bg-gray-200 sm:text-gray-600 sm:hover:bg-gray-300 text-gray-600 hover:text-gray-800'
                    }`}
                  title={bookmarked ? 'Đã đánh dấu' : 'Đánh dấu'}
                >
                  <Bookmark className={`w-3.5 h-3.5 sm:w-3.5 sm:h-3.5 ${bookmarked ? 'fill-current' : ''}`} />
                </button>

                <button
                  onClick={handleShare}
                  className="flex items-center justify-center p-0.5 sm:p-1.5 sm:rounded sm:bg-gray-200 sm:text-gray-600 sm:hover:bg-gray-300 sm:transition-colors text-gray-600 hover:text-gray-800"
                  title="Chia sẻ"
                >
                  <Share2 className="w-3.5 h-3.5 sm:w-3.5 sm:h-3.5" />
                </button>

                <Link
                  to={`/lop/${idLop}/chuong/${idChuong}`}
                  className="flex items-center justify-center p-0.5 sm:p-1.5 sm:rounded sm:bg-orange-500 sm:text-white sm:hover:bg-orange-600 sm:transition-colors text-gray-600 hover:text-gray-800"
                  title="Về danh sách bài"
                >
                  <ArrowLeft className="w-3.5 h-3.5 sm:w-3.5 sm:h-3.5" />
                </Link>
              </div>
            </div>
          </div>

          {/* Minigame Section - Riêng biệt phía trên tabs */}
          {hasGameQuestions(
            lopNumber,
            chuongNumber,
            baiNumber
          ) && (
              <motion.div
                className="mb-3 sm:mb-4 relative"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <motion.button
                  onClick={() => setShowPhysicsSurvivalGame(true)}
                  className="relative w-full bg-gradient-to-r from-orange-500 via-orange-400 to-amber-400 hover:from-orange-600 hover:via-orange-500 hover:to-amber-500 rounded-lg sm:rounded-xl transition-all p-3 sm:p-4 shadow-lg"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.95 }}
                  animate={{
                    scale: [1, 1.05, 1]
                  }}
                  transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 sm:gap-3">
                      <motion.div
                        animate={{
                          rotate: [0, 5, -5, 0],
                          scale: [1, 1.1, 1]
                        }}
                        transition={{
                          duration: 2,
                          repeat: Infinity,
                          ease: "easeInOut"
                        }}
                      >
                        <Gamepad2 className="w-6 h-6 sm:w-8 sm:h-8 text-white shrink-0" />
                      </motion.div>
                      <div className="text-left">
                        <motion.h3
                          className="text-base sm:text-xl font-bold text-white"
                          animate={{
                            textShadow: [
                              "0 0 10px rgba(255, 255, 255, 0.5)",
                              "0 0 20px rgba(255, 255, 255, 0.8)",
                              "0 0 10px rgba(255, 255, 255, 0.5)"
                            ]
                          }}
                          transition={{
                            duration: 2,
                            repeat: Infinity,
                            ease: "easeInOut"
                          }}
                        >
                          🎮 Minigame PHYSICS SURVIVAL RUNNER
                        </motion.h3>
                        <p className="text-xs sm:text-sm text-white/90">
                          10 câu hỏi · 1-3 phút · Đường đua tri thức!
                        </p>
                      </div>
                    </div>
                    <motion.div
                      className="flex items-center gap-2 text-white"
                      animate={{ x: [0, 5, 0] }}
                      transition={{
                        duration: 1.5,
                        repeat: Infinity,
                        ease: "easeInOut"
                      }}
                    >
                      <span className="hidden sm:inline text-sm font-semibold">Bắt đầu</span>
                      <Play className="w-5 h-5 sm:w-6 sm:h-6" />
                    </motion.div>
                  </div>
                </motion.button>

                {/* Game History - Hiển thị lịch sử chơi */}
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  transition={{ delay: 0.3 }}
                  className="mt-3"
                >
                  <GameHistory
                    gameId={`survival-runner-lop-${lopNumber}-chuong-${chuongNumber}-bai-${baiNumber}`}
                    limit={3}
                  />
                </motion.div>
              </motion.div>
            )}

          {/* Section Tabs - Desktop và Mobile */}
          <div className="mb-2 sm:mb-4">
            {/* Desktop Tabs - Full width */}
            <div className="hidden sm:flex bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
              <button
                onClick={() => handleSectionChange('lyThuyet')}
                className={`flex-1 px-4 py-3 font-medium text-sm transition-all duration-300 flex items-center justify-center space-x-2 ${currentSection === 'lyThuyet'
                  ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
              >
                <BookOpen className="w-4 h-4" />
                <span>Lý thuyết</span>
              </button>
              <button
                onClick={() => setCurrentSection('video')}
                className={`flex-1 px-4 py-3 font-medium text-sm transition-all duration-300 flex items-center justify-center space-x-2 border-l ${currentSection === 'video'
                  ? 'bg-gradient-to-r from-red-500 to-red-600 text-white shadow-lg border-red-400'
                  : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-200'
                  }`}
              >
                <Video className="w-4 h-4" />
                <span>Video YouTube</span>
              </button>
              <button
                onClick={() => handleSectionChange('infographic')}
                className={`flex-1 px-4 py-3 font-medium text-sm transition-all duration-300 flex items-center justify-center space-x-2 border-l ${currentSection === 'infographic'
                  ? 'bg-gradient-to-r from-indigo-500 to-indigo-600 text-white shadow-lg border-indigo-400'
                  : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-200'
                  }`}
              >
                <Sparkles className="w-4 h-4" />
                <span>Infographic AI</span>
              </button>
              <button
                onClick={() => setCurrentSection('moPhong')}
                className={`flex-1 px-4 py-3 font-medium text-sm transition-all duration-300 flex items-center justify-center space-x-2 border-l ${currentSection === 'moPhong'
                  ? 'bg-gradient-to-r from-purple-500 to-purple-600 text-white shadow-lg border-purple-400'
                  : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-200'
                  }`}
              >
                <Gamepad2 className="w-4 h-4" />
                <span>Mô phỏng</span>
              </button>
              <button
                onClick={() => handleSectionChange('tracNghiem')}
                className={`flex-1 px-4 py-3 font-medium text-sm transition-all duration-300 flex items-center justify-center space-x-2 border-l ${currentSection === 'tracNghiem'
                  ? 'bg-gradient-to-r from-green-500 to-green-600 text-white shadow-lg border-green-400'
                  : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-200'
                  }`}
              >
                <Target className="w-4 h-4" />
                <span>Trắc nghiệm</span>
              </button>
            </div>

            {/* Mobile Tabs - Custom Grid Layout (3 top, 2 bottom) */}
            <div className="sm:hidden bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
              <div className="grid grid-cols-6 gap-0">
                {/* Row 1: 3 items (col-span-2 each) */}
                <button
                  onClick={() => handleSectionChange('lyThuyet')}
                  className={`col-span-2 px-1 py-2.5 font-medium text-[10px] transition-all duration-300 flex flex-col items-center justify-center gap-1 border-b border-r ${currentSection === 'lyThuyet'
                    ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white'
                    : 'bg-white text-gray-700'
                    }`}
                >
                  <BookOpen className="w-4 h-4" />
                  <span>Lý thuyết</span>
                </button>
                <button
                  onClick={() => setCurrentSection('video')}
                  className={`col-span-2 px-1 py-2.5 font-medium text-[10px] transition-all duration-300 flex flex-col items-center justify-center gap-1 border-b border-r ${currentSection === 'video'
                    ? 'bg-gradient-to-r from-red-500 to-red-600 text-white'
                    : 'bg-white text-gray-700'
                    }`}
                >
                  <Video className="w-4 h-4" />
                  <span>Video</span>
                </button>
                <button
                  onClick={() => handleSectionChange('infographic')}
                  className={`col-span-2 px-1 py-2.5 font-medium text-[10px] transition-all duration-300 flex flex-col items-center justify-center gap-1 border-b ${currentSection === 'infographic'
                    ? 'bg-gradient-to-r from-indigo-500 to-indigo-600 text-white'
                    : 'bg-white text-gray-700'
                    }`}
                >
                  <Sparkles className="w-4 h-4" />
                  <span>Infographic</span>
                </button>

                {/* Row 2: 2 items (col-span-3 each) */}
                <button
                  onClick={() => setCurrentSection('moPhong')}
                  className={`col-span-3 px-2 py-2.5 font-medium text-[10px] transition-all duration-300 flex items-center justify-center space-x-1.5 border-r ${currentSection === 'moPhong'
                    ? 'bg-gradient-to-r from-purple-500 to-purple-600 text-white'
                    : 'bg-white text-gray-700'
                    }`}
                >
                  <Gamepad2 className="w-3.5 h-3.5" />
                  <span>Mô phỏng</span>
                </button>
                <button
                  onClick={() => handleSectionChange('tracNghiem')}
                  className={`col-span-3 px-2 py-2.5 font-medium text-[10px] transition-all duration-300 flex items-center justify-center space-x-1.5 ${currentSection === 'tracNghiem'
                    ? 'bg-gradient-to-r from-green-500 to-green-600 text-white'
                    : 'bg-white text-gray-700'
                    }`}
                >
                  <Target className="w-3.5 h-3.5" />
                  <span>Trắc nghiệm</span>
                </button>
              </div>
            </div>
          </div>

          {/* Lesson Content - Focused */}
          <div className="lesson-content bg-white rounded-lg sm:rounded-xl shadow-md p-2 sm:p-4">
            {/* Section header removed for more content space */}

            {/* Content Errors Summary - Chỉ hiển thị khi có lỗi nghiêm trọng */}
            {contentErrors.filter(e => e.severity === 'critical').length > 0 && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center space-x-2 mb-2">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                  <span className="font-medium text-red-800">
                    Có {contentErrors.filter(e => e.severity === 'critical').length} lỗi nghiêm trọng hiển thị nội dung
                  </span>
                </div>
                <p className="text-sm text-red-700">
                  Một số phần nội dung không thể hiển thị. Vui lòng thử tải lại trang hoặc liên hệ hỗ trợ.
                </p>
              </div>
            )}

            {/* Render content based on current section */}
            {currentSection === 'lyThuyet' && (
              <div className="text-mobile-body prose prose-sm sm:prose-lg max-w-none">
                {/* Overall Progress Bar - Minimal */}
                {lessonData?.noiDung && (
                  <div className="sticky top-0 z-20 bg-white border-b border-gray-200 mb-1 sm:mb-2 -mx-2 sm:-mx-4 px-1 sm:px-2 py-0.5 sm:py-1">
                    <div className="w-full bg-gray-200 rounded-full h-1 sm:h-1.5 overflow-hidden">
                      <div
                        className="bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 h-1 sm:h-1.5 rounded-full transition-all duration-500 ease-out"
                        style={{ width: `${overallProgress}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* Render all sections: phanMoDau, phanNoiDung, phanCuoi - Mobile Optimized */}
                {lessonData?.noiDung ? (
                  <div>
                    {/* Section 1: Phần Mở đầu - Minimal Spacing */}
                    {lessonData.noiDung.phanMoDau?.noiDung && lessonData.noiDung.phanMoDau.noiDung.length > 0 && (
                      <div className="mb-2 sm:mb-6">
                        <div className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-2 sm:px-4 py-1 sm:py-3 rounded-t-md sm:rounded-t-lg">
                          <h3 className="text-sm sm:text-lg font-bold flex items-center">
                            <span className="text-base sm:text-xl mr-1 sm:mr-2">📘</span>
                            <span className="truncate">{lessonData.noiDung.phanMoDau.tieuDe}</span>
                          </h3>
                        </div>
                        <div
                          ref={moDauSectionRef}
                          data-summary-section="phanMoDau"
                          className={`border border-blue-200 border-t-0 rounded-b-md sm:rounded-b-lg p-2 sm:p-4 bg-white ${getSummaryHighlightClass('phanMoDau')}`}
                        >
                          <InteractiveContentRenderer
                            content={lessonData.noiDung.phanMoDau.noiDung}
                            lessonId={lessonId} // ✅ FIX: Sử dụng lessonId duy nhất
                            onSectionComplete={() => handleSectionComplete('phanMoDau')} // ✅ NEW
                            onFirstInteraction={async () => {
                              // ✅ Kiểm tra đăng nhập và thông tin user khi tương tác lần đầu
                              console.log('🎯 [XemBaiHoc] First interaction in phanMoDau, checking user info');
                              if (!user) {
                                setShowLoginModal(true); // Hiện modal đăng nhập
                                return;
                              }
                              // ⚠️ TEMPORARY: Disable user info check
                              // await checkUserInfo();
                            }}
                            onProgress={(progress) => {
                              const totalSections = 3
                              const sectionWeight = 100 / totalSections
                              const section1Progress = (progress / 100) * sectionWeight
                              setOverallProgress(section1Progress)
                            }}
                          />
                        </div>
                      </div>
                    )}

                    {/* Section 2: Phần Nội dung - Minimal Spacing */}
                    {lessonData.noiDung.phanNoiDung?.noiDung && lessonData.noiDung.phanNoiDung.noiDung.length > 0 && (
                      <div className="mb-2 sm:mb-6">
                        <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-2 sm:px-4 py-1 sm:py-3 rounded-t-md sm:rounded-t-lg">
                          <h3 className="text-sm sm:text-lg font-bold flex items-center">
                            <span className="text-base sm:text-xl mr-1 sm:mr-2">📗</span>
                            <span className="truncate">{lessonData.noiDung.phanNoiDung.tieuDe}</span>
                          </h3>
                        </div>
                        <div
                          ref={noiDungSectionRef}
                          data-summary-section="phanNoiDung"
                          className={`border border-green-200 border-t-0 rounded-b-md sm:rounded-b-lg p-2 sm:p-4 bg-white ${getSummaryHighlightClass('phanNoiDung')}`}
                        >
                          <InteractiveContentRenderer
                            content={lessonData.noiDung.phanNoiDung.noiDung}
                            lessonId={lessonId} // ✅ FIX: Sử dụng lessonId duy nhất
                            onSectionComplete={() => handleSectionComplete('phanNoiDung')} // ✅ NEW
                            onFirstInteraction={async () => {
                              // ✅ Kiểm tra đăng nhập và thông tin user khi tương tác lần đầu
                              console.log('🎯 [XemBaiHoc] First interaction in phanNoiDung, checking user info');
                              if (!user) {
                                setShowLoginModal(true); // Hiện modal đăng nhập
                                return;
                              }
                              // ⚠️ TEMPORARY: Disable user info check
                              // await checkUserInfo();
                            }}
                            onProgress={(progress) => {
                              const totalSections = 3
                              const sectionWeight = 100 / totalSections
                              const section2Progress = sectionWeight + (progress / 100) * sectionWeight
                              setOverallProgress(section2Progress)
                            }}
                          />
                        </div>
                      </div>
                    )}

                    {/* Section 3: Phần Cuối - Minimal Spacing */}
                    {lessonData.noiDung.phanCuoi?.noiDung && lessonData.noiDung.phanCuoi.noiDung.length > 0 && (
                      <div className="mb-2 sm:mb-6">
                        <div className="bg-gradient-to-r from-purple-500 to-pink-600 text-white px-2 sm:px-4 py-1 sm:py-3 rounded-t-md sm:rounded-t-lg">
                          <h3 className="text-sm sm:text-lg font-bold flex items-center">
                            <span className="text-base sm:text-xl mr-1 sm:mr-2">📙</span>
                            <span className="truncate">{lessonData.noiDung.phanCuoi.tieuDe}</span>
                          </h3>
                        </div>
                        <div
                          ref={phanCuoiSectionRef}
                          data-summary-section="phanCuoi"
                          className={`border border-purple-200 border-t-0 rounded-b-md sm:rounded-b-lg p-2 sm:p-4 bg-white ${getSummaryHighlightClass('phanCuoi')}`}
                        >
                          <InteractiveContentRenderer
                            content={lessonData.noiDung.phanCuoi.noiDung}
                            lessonId={lessonId} // ✅ FIX: Sử dụng lessonId duy nhất
                            onSectionComplete={() => handleSectionComplete('phanCuoi')} // ✅ NEW
                            onFirstInteraction={async () => {
                              // ✅ Kiểm tra đăng nhập và thông tin user khi tương tác lần đầu
                              console.log('🎯 [XemBaiHoc] First interaction in phanCuoi, checking user info');
                              if (!user) {
                                setShowLoginModal(true); // Hiện modal đăng nhập
                                return;
                              }
                              // ⚠️ TEMPORARY: Disable user info check
                              // await checkUserInfo();
                            }}
                            onProgress={(progress) => {
                              const totalSections = 3
                              const sectionWeight = 100 / totalSections
                              const section3Progress = (2 * sectionWeight) + (progress / 100) * sectionWeight
                              setOverallProgress(section3Progress)
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <ContentRenderer
                    content={baiHocSections?.lyThuyet?.noiDung || []}
                    onError={handleContentError}
                  />
                )}
                <div ref={summaryTriggerRef} className="h-4 w-full" />
              </div>
            )}

            {currentSection === 'infographic' && lessonData && (
              <div className="min-h-[500px]">
                <InfographicTab
                  lessonTitle={lessonData.tieuDe}
                  lessonContent={JSON.stringify(lessonData.noiDung)}
                  lessonId={lessonId}
                  lop={lessonSlugs.lop}
                  chuong={lessonSlugs.chuong}
                  bai={lessonSlugs.bai}
                />
              </div>
            )}

            {currentSection === 'video' && (
              <div className="space-y-4 sm:space-y-6">
                <div className="bg-gray-50 rounded-lg p-3 sm:p-6">
                  <h3 className="text-base sm:text-lg font-semibold text-blue-700 mb-2 sm:mb-4 flex items-center">
                    <Video className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                    Video giảng dạy
                  </h3>
                  <p className="text-sm sm:text-base text-gray-600 mb-3 sm:mb-4">{baiHocSections.video.moTa}</p>

                  {/* YouTube Video Embed - Mobile Optimized */}
                  <div className="video-container-mobile relative w-full h-0 pb-[56.25%] rounded-lg overflow-hidden bg-gray-100 youtube-container">
                    {(() => {
                      const url = baiHocSections.video.youtubeUrl
                      const videoIdMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/)
                      const videoId = videoIdMatch ? videoIdMatch[1] : null

                      if (!videoId) {
                        return (
                          <div className="absolute inset-0 flex items-center justify-center bg-gray-100 text-gray-600 text-center p-4">
                            <div>
                              <Video className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                              <p className="text-sm mb-2 font-medium">Video không hợp lệ</p>
                              <a
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center px-3 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition-colors"
                              >
                                <Video className="w-4 h-4 mr-2" />
                                Xem trên YouTube
                              </a>
                            </div>
                          </div>
                        )
                      }

                      // Always try iframe first, with multiple fallback strategies
                      return (
                        <>
                          <iframe
                            className="absolute top-0 left-0 w-full h-full"
                            src={`https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1&showinfo=0&enablejsapi=1&origin=${window.location.origin}`}
                            title="Video giảng dạy"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen"
                            allowFullScreen
                            referrerPolicy="no-referrer-when-downgrade"
                            sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
                            onLoad={() => {
                              console.log('✅ YouTube iframe loaded successfully')
                              // Hide fallback if iframe loads successfully
                              const fallback = document.getElementById('youtube-fallback')
                              if (fallback) {
                                fallback.style.display = 'none'
                              }
                            }}
                            onError={() => {
                              console.warn('❌ YouTube iframe failed to load')
                              // Show fallback after iframe fails
                              const fallback = document.getElementById('youtube-fallback')
                              if (fallback) {
                                fallback.style.display = 'flex'
                              }
                            }}
                          ></iframe>

                          {/* Fallback message - initially hidden */}
                          <div
                            id="youtube-fallback"
                            className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-red-50 to-red-100 text-gray-600 text-center p-4"
                            style={{ display: 'none' }}
                          >
                            <div>
                              <div className="w-20 h-20 mx-auto mb-4 bg-red-600 rounded-full flex items-center justify-center">
                                <Video className="w-10 h-10 text-white" />
                              </div>
                              <h3 className="text-lg font-semibold text-blue-700 mb-2">Video không thể hiển thị</h3>
                              <p className="text-sm text-gray-600 mb-4">Có thể do cài đặt bảo mật của server</p>
                              <div className="space-y-2">
                                <a
                                  href={url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition-colors mr-2"
                                >
                                  <Video className="w-4 h-4 mr-2" />
                                  Xem trên YouTube
                                </a>
                                <button
                                  onClick={() => {
                                    // Try to reload iframe
                                    const iframe = document.querySelector('iframe[title="Video giảng dạy"]') as HTMLIFrameElement
                                    if (iframe) {
                                      const currentSrc = iframe.src
                                      iframe.src = ''
                                      setTimeout(() => {
                                        iframe.src = currentSrc
                                      }, 100)
                                    }
                                  }}
                                  className="inline-flex items-center px-4 py-2 bg-gray-600 text-white text-sm rounded-lg hover:bg-gray-700 transition-colors"
                                >
                                  🔄 Thử lại
                                </button>
                              </div>
                            </div>
                          </div>
                        </>
                      )
                    })()}
                  </div>

                  {/* Video Actions - Mobile Optimized */}
                  <div className="mt-3 sm:mt-4 flex flex-col sm:flex-row gap-2 sm:gap-3">
                    <a
                      href={baiHocSections.video.youtubeUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-secondary inline-flex items-center justify-center px-3 sm:px-4 py-2 rounded-lg text-sm sm:text-base"
                    >
                      <Video className="w-3 h-3 sm:w-4 sm:h-4 mr-2" />
                      <span>Xem trên YouTube</span>
                    </a>

                    <button
                      onClick={() => {
                        // Copy link to clipboard
                        navigator.clipboard.writeText(baiHocSections.video.youtubeUrl)
                        toast.success('Đã sao chép link video!')
                      }}
                      className="btn-soft inline-flex items-center justify-center px-3 sm:px-4 py-2 rounded-lg text-sm sm:text-base"
                    >
                      <Share2 className="w-3 h-3 sm:w-4 sm:h-4 mr-2" />
                      <span>Sao chép link</span>
                    </button>

                    <button
                      onClick={() => {
                        // Reload iframe
                        const iframe = document.querySelector('iframe[title="Video giảng dạy"]') as HTMLIFrameElement
                        if (iframe) {
                          const currentSrc = iframe.src
                          iframe.src = ''
                          setTimeout(() => {
                            iframe.src = currentSrc
                          }, 100)
                          toast.success('Đã tải lại video!')
                        }
                      }}
                      className="btn-soft inline-flex items-center justify-center px-3 sm:px-4 py-2 rounded-lg text-sm sm:text-base"
                    >
                      <span className="mr-2">🔄</span>
                      <span>Tải lại</span>
                    </button>
                  </div>
                </div>
              </div>
            )}

            {currentSection === 'tracNghiem' && !isQuizFullscreen && (
              showQuizInterface && (bankQuizData || quizData) ? (
                <QuizInterface
                  key={quizKey} // Force remount khi quizKey thay đổi
                  quizData={bankQuizData || quizData}
                  lessonId={`lop-${idLop}-chuong-${idChuong}-bai-${idBai}`}
                  onBack={() => {
                    console.log('🔙 [NON-FULLSCREEN] QuizInterface onBack called')

                    // Force thoát browser fullscreen nếu đang fullscreen
                    if (document.fullscreenElement) {
                      document.exitFullscreen().catch(err => {
                        console.log('Exit fullscreen failed:', err)
                      })
                    }

                    setShowQuizInterface(false)
                    setIsQuizFullscreen(false) // Đảm bảo thoát fullscreen mode
                    setQuizKey(prev => prev + 1) // Tăng key để reset component lần sau
                    setCurrentSection('lyThuyet') // Quay về tab lý thuyết sau khi nộp bài
                    window.scrollTo({ top: 0, behavior: 'smooth' }) // Scroll lên đầu trang
                  }}
                  onFullscreenChange={setIsQuizFullscreen}
                  forceDarkMode={true}
                />
              ) : !showQuizInterface ? (
                <div className="space-y-6">
                  <div className="bg-gradient-to-br from-orange-50 to-blue-50 rounded-lg p-6 border border-orange-200">
                    <h3 className="text-lg font-semibold text-orange-900 mb-4">🎯 Bài tập trắc nghiệm</h3>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* Question Statistics */}
                      <QuestionStats
                        lop={idLop || '10'}
                        chuong={idChuong || '1'}
                        bai={(() => {
                          let baiParam = idBai || '1'
                          if (baiParam.startsWith('bai-')) {
                            baiParam = baiParam.replace('bai-', '')
                          }
                          // Trích xuất số bài từ chuỗi như "1-dao-dong-dieu-hoa-..."
                          const match = baiParam.match(/^(\d+)/)
                          return match ? match[1] : '1'
                        })()}
                        teacherId={teacherId} // ✅ FIX: Truyền teacherId để load thống kê từ teacher database
                        preloadedDatabases={teacherDatabasesPreloaded} // ✅ FIX: Truyền preloaded databases để tránh load lại
                      />

                      {/* Quiz Action */}
                      <div className="bg-white rounded-xl p-6 border border-light-200 shadow-sm">
                        {bankQuizData || quizData ? (
                          <div className="text-center">
                            <div className="mb-6">
                              <h4 className="text-lg font-semibold text-blue-700 mb-3">
                                {bankQuizData ? '🎯 Bài kiểm tra từ ngân hàng câu hỏi' : '📝 Bài kiểm tra từ nội dung'}
                              </h4>
                              <p className="text-gray-600 mb-4">
                                {bankQuizData ?
                                  `Bài kiểm tra gồm ${bankQuizData.cauHoi.length} câu hỏi được chọn ngẫu nhiên` :
                                  'Bài kiểm tra từ nội dung bài học'
                                }
                              </p>
                              {bankQuizData && (
                                <div className="flex justify-center space-x-6 text-sm text-gray-600 mb-4">
                                  <div className="flex items-center">
                                    <Clock className="w-4 h-4 mr-1" />
                                    <span>{Math.floor(bankQuizData.gioi_han_thoi_gian / 60)} phút</span>
                                  </div>
                                  <div className="flex items-center">
                                    <Target className="w-4 h-4 mr-1" />
                                    <span>Điểm đạt: {bankQuizData.diem_dat}%</span>
                                  </div>
                                  <div className="flex items-center">
                                    <Brain className="w-4 h-4 mr-1" />
                                    <span>{bankQuizData.cauHoi.length} câu hỏi</span>
                                  </div>
                                </div>
                              )}
                            </div>
                            <button
                              onClick={async () => {
                                // ✅ Bước 1: Yêu cầu đăng nhập với Google nếu chưa đăng nhập
                                if (!user) {
                                  setShowLoginModal(true); // Hiện modal đăng nhập
                                  return;
                                }

                                // ✅ FIX: Set showQuizInterface ngay lập tức để tránh treo UI
                                setShowQuizInterface(true)

                                // ✅ Bước 2: Kiểm tra thông tin user sau khi đã hiển thị giao diện (non-blocking)
                                // Nếu cần thông tin, QuizInterface sẽ tự xử lý
                                checkUserInfo().then(needsInfo => {
                                  if (needsInfo) {
                                    // Nếu cần thông tin, QuizInterface sẽ tự quay lại
                                    console.log('⚠️ [XemBaiHoc] User needs info, QuizInterface will handle')
                                  }
                                }).catch(err => {
                                  console.error('Error checking user info:', err)
                                })

                                // ✅ FIX: Reload quiz từ teacher database sau khi đã hiển thị giao diện (non-blocking)
                                if (teacherId) {
                                  console.log('👨‍🏫 [Phần 4 - Làm bài] Reloading quiz from teacher database (non-blocking)...')
                                    // Chạy async không block UI
                                    ; (async () => {
                                      try {
                                        const lopParam = idLop || '10'
                                        const chuongParam = idChuong || '1'
                                        let baiParam = idBai || '1'
                                        if (baiParam.startsWith('bai-')) {
                                          baiParam = baiParam.replace('bai-', '')
                                        }
                                        const match = baiParam.match(/^(\d+)/)
                                        const baiNum = match ? match[1] : '1'

                                        // Load databases từ server hoặc IndexedDB
                                        let tnDb: Uint8Array | null = null
                                        let dsDb: Uint8Array | null = null
                                        let tlDb: Uint8Array | null = null

                                        // ✅ FIX: Check database availability trước (sử dụng cache)
                                        const availability = await databaseAvailabilityService.checkDatabaseAvailability(teacherId, lopParam, chuongParam, baiNum)
                                        console.log('📊 [Phần 4 - Làm bài] Database availability:', availability)

                                        // Load databases từ server (chỉ nếu có database từ server)
                                        if (availability.hasServerDatabase) {
                                          try {
                                            const serverDbs = await teacherContentService.getTeacherDatabases(teacherId, lopParam, chuongParam, baiNum, 3000)
                                            tnDb = serverDbs.TN
                                            dsDb = serverDbs.DS
                                            tlDb = serverDbs.TL
                                          } catch (e) {
                                            console.warn('⚠️ [Phần 4 - Làm bài] Failed to fetch databases from server:', e)
                                          }
                                        } else {
                                          console.log('⚠️ [Phần 4 - Làm bài] No server database available (from cache), skipping server fetch')
                                        }

                                        if ((!tnDb || tnDb.length === 0) && (!dsDb || dsDb.length === 0) && (!tlDb || tlDb.length === 0)) {
                                          console.log('⚠️ [Phần 4 - Làm bài] No server databases found, checking local IndexedDB...')
                                          const localDbs = await Promise.all([
                                            teacherContentService.loadDatabaseFromIndexedDB(teacherId, lopParam, chuongParam, baiNum, 'TN').catch(() => null),
                                            teacherContentService.loadDatabaseFromIndexedDB(teacherId, lopParam, chuongParam, baiNum, 'DS').catch(() => null),
                                            teacherContentService.loadDatabaseFromIndexedDB(teacherId, lopParam, chuongParam, baiNum, 'TL').catch(() => null)
                                          ])
                                          tnDb = localDbs[0]
                                          dsDb = localDbs[1]
                                          tlDb = localDbs[2]
                                        }

                                        const hasTeacherData = (tnDb && tnDb.length > 0) || (dsDb && dsDb.length > 0) || (tlDb && tlDb.length > 0)
                                        if (hasTeacherData) {
                                          console.log('✅ [Phần 4 - Làm bài] Teacher databases found, reloading quiz...')
                                          const reloadedQuiz = await bankQuestionService.getRandomQuestions(
                                            lopParam,
                                            chuongParam,
                                            baiNum,
                                            {
                                              thoiGianLamBai: 15,
                                              diemDat: 70
                                            },
                                            teacherId,
                                            { TN: tnDb, DS: dsDb, TL: tlDb }
                                          )
                                          setBankQuizData(reloadedQuiz)
                                          setQuizKey(prev => prev + 1) // Force remount QuizInterface
                                          console.log('✅ [Phần 4 - Làm bài] Quiz reloaded from teacher database:', reloadedQuiz.cauHoi.length, 'questions')
                                        } else {
                                          console.warn('⚠️ [Phần 4 - Làm bài] No teacher databases found, using existing quiz data')
                                        }
                                      } catch (error) {
                                        console.error('❌ [Phần 4 - Làm bài] Error reloading quiz from teacher database:', error)
                                        // Continue với quiz hiện tại
                                      }
                                    })()
                                }

                                // Auto enter fullscreen when starting quiz
                                setTimeout(() => {
                                  if (!document.fullscreenElement) {
                                    document.documentElement.requestFullscreen().catch(err => {
                                      console.log('Fullscreen request failed:', err)
                                    })
                                    setIsQuizFullscreen(true)
                                  }
                                }, 100)
                              }}
                              className="btn-primary w-full px-8 py-3 font-semibold rounded-xl"
                            >
                              🚀 Bắt đầu làm bài
                            </button>
                          </div>
                        ) : (
                          <div className="text-center text-gray-600">
                            <div className="mb-4">
                              <Brain className="w-16 h-16 text-gray-400 mx-auto mb-3" />
                              <h4 className="text-lg font-semibold text-blue-700 mb-2">Chưa có bài kiểm tra</h4>
                              <p>Vui lòng tạo bài kiểm tra từ ngân hàng câu hỏi bên trái.</p>
                              {/* Debug info */}
                              <div className="mt-4 p-2 bg-gray-100 rounded text-xs">
                                <p>Debug: bankQuizData = {bankQuizData ? 'Có' : 'Không'}</p>
                                <p>Debug: quizData = {quizData ? 'Có' : 'Không'}</p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ) : null
            )}

            {currentSection === 'moPhong' && (
              <div className="space-y-6">
                <div className="bg-green-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-green-900 mb-4 flex items-center">
                    <Gamepad2 className="w-5 h-5 mr-2" />
                    {baiHocSections.moPhong.ten}
                  </h3>
                  <p className="text-green-700 mb-4">{baiHocSections.moPhong.moTa}</p>

                  {/* 1️⃣ TEACHER/SYSTEM VIDEO */}
                  {/* Teacher Simulation Video - Ưu tiên hiển thị nếu có */}
                  {(() => {
                    const teacherSim = teacherSimulationContent
                    const simulationUrl = baiHocSections?.moPhong?.url || ''
                    let simulationSrc = simulationUrl
                    let simulationTitle = 'Video mô phỏng từ giáo viên'
                    let simulationDescription = ''

                    if (teacherSim?.contentData) {
                      const simData = teacherSim.contentData
                      const teacherSimulation = simData.url ? simData : (simData.items?.[0] || null)

                      if (teacherSimulation) {
                        let rawSrc = teacherSimulation.url || teacherSimulation.content || teacherSimulation.src || simulationUrl

                        if (rawSrc && !rawSrc.startsWith('http') && !rawSrc.startsWith('data:') && !rawSrc.startsWith('blob:')) {
                          if (rawSrc.startsWith('/teacher-content/') || rawSrc.startsWith('teacher-content/')) {
                            const cleanSrc = rawSrc.startsWith('/') ? rawSrc : `/${rawSrc}`
                            const hasStructuredPath = /\/L\d+\/C\d+\/B\d+\//.test(cleanSrc)

                            if (!hasStructuredPath) {
                              // Extract teacherId và filename
                              const teacherIdMatch = cleanSrc.match(/\/teacher-content\/([^\/]+)/)
                              const pathParts = cleanSrc.split('/').filter(p => p)
                              const filename = pathParts.pop() || ''

                              if (teacherIdMatch && idLop && idChuong && idBai) {
                                const teacherId = teacherIdMatch[1]
                                const lopNum = parseInt(idLop) || 11
                                const chuongNum = parseInt(idChuong.replace('chuong-', '').split('-')[0]) || 1
                                const baiNum = parseInt(idBai.replace('bai-', '').split('-')[0]) || 1
                                const baiPadded = baiNum.toString().padStart(2, '0')

                                rawSrc = `/teacher-content/${teacherId}/L${lopNum}/C${chuongNum}/B${baiPadded}/${filename}`
                                console.log(`🔧 [Simulation Path Fix] Thêm L/C/B: ${rawSrc}`)
                              }
                            }
                          }
                        }

                        simulationSrc = rawSrc
                        simulationTitle = teacherSimulation.title || simData.title || simulationTitle
                        simulationDescription = teacherSimulation.description || simData.description || ''
                      }
                    }

                    if (simulationSrc && simulationSrc.trim() !== '' && simulationSrc !== '#') {
                      let finalSimulationSrc = simulationSrc
                      if (simulationSrc.includes('/wp-content/uploads/teacher-content/')) {
                        const match = simulationSrc.match(/\/wp-content\/uploads\/(teacher-content\/.+)$/)
                        if (match) finalSimulationSrc = `/${match[1]}`
                      }

                      return (
                        <div className="bg-white rounded-lg border-2 border-purple-300 overflow-hidden mb-8">
                          <div className="bg-gradient-to-r from-purple-500 to-purple-600 text-white px-4 py-2">
                            <h4 className="text-sm font-semibold flex items-center gap-2">
                              <Database className="w-4 h-4" />
                              Video mô phỏng từ giáo viên
                            </h4>
                          </div>
                          <div className="p-4">
                            <ContentRenderer
                              content={[{
                                type: 'video',
                                content: finalSimulationSrc,
                                src: finalSimulationSrc,
                                title: simulationTitle,
                                description: simulationDescription
                              } as any]}
                            />
                          </div>
                        </div>
                      )
                    }
                    return null
                  })()}

                  {/* System Simulation Video (Fallback) */}
                  {(() => {
                    const teacherSim = teacherSimulationContent
                    const hasTeacherSimulation =
                      teacherSim?.contentData?.src ||
                      teacherSim?.contentData?.items?.[0]?.content ||
                      teacherSim?.contentData?.items?.[0]?.src ||
                      teacherSim?.contentData?.url

                    // Chỉ hiển thị System Video nếu KHÔNG có Teacher Video
                    if (hasTeacherSimulation) return null;

                    return (
                      <div className="mb-8">
                        {simulationLoading ? (
                          <div className="bg-white p-12 rounded-lg border border-green-200 text-center">
                            <LoadingSpinner />
                            <p className="text-gray-600 mt-4">Đang tải video mô phỏng...</p>
                          </div>
                        ) : simulationError ? (
                          <div className="bg-white p-6 rounded-lg border border-red-200 text-center">
                            <p className="text-red-500">{simulationError}</p>
                            <button onClick={() => idLop && idChuong && idBai && loadSimulationComponent(idLop, idChuong, idBai)} className="mt-2 text-blue-600 underline">Thử lại</button>
                          </div>
                        ) : SimulationComponent ? (
                          <Suspense fallback={<LoadingSpinner />}>
                            <SimulationComponent
                              title={baiHocSections.moPhong.ten}
                              description={baiHocSections.moPhong.moTa || 'Video mô phỏng vật lý'}
                              videoPath={generateVideoPath(idLop || '10', idChuong || '1', idBai || 'bai-1')}
                              videos={(() => {
                                const lop = parseInt(idLop || '10')
                                const chuong = parseInt(idChuong || '1')
                                let bai = 1
                                if (idBai) {
                                  const match = idBai.match(/bai-(\d+)/) || idBai.match(/^(\d+)/)
                                  if (match) bai = parseInt(match[1])
                                }
                                const videos = VideoService.getVideosForLesson(lop, chuong, bai)
                                return videos.map(v => ({
                                  title: v.title,
                                  description: 'Video mô phỏng bài học',
                                  videoPath: v.videoPath
                                }))
                              })()}
                            />
                          </Suspense>
                        ) : (
                          // Không có video system lẫn teacher -> Không hiển thị gì ở phần video (để dành chỗ cho GeoGebra)
                          null
                        )}
                      </div>
                    )
                  })()}

                  {/* 2️⃣ GEOGEBRA SIMULATION (NEW) */}
                  {(() => {
                    // Tính toán Class/Chapter/Lesson để lấy GeoGebra file
                    const lop = parseInt(idLop || '10');
                    const chuong = parseInt(idChuong?.replace('chuong-', '').split('-')[0] || '1');
                    let bai = 1;
                    if (idBai) {
                      const match = idBai.match(/bai-(\d+)/) || idBai.match(/^(\d+)/);
                      if (match) bai = parseInt(match[1]);
                    }

                    // Import Service & Component dynamically or use if imported at top (assumed top import)
                    // Ở đây tôi giả định bạn đã import GeoGebraService và GeoGebraSimulation ở trên cùng file
                    // Nhưng nếu chưa, ta phải dùng dynamic import hoặc giả định có sẵn.
                    // Để an toàn, tôi sẽ hiển thị logic thôi.

                    const geoGebraFiles = GeoGebraService.getAll().filter(item =>
                      item.lop === lop && item.chuong === chuong && item.bai === bai
                    );

                    if (geoGebraFiles.length > 0) {
                      return (
                        <div className="space-y-6 mt-8 border-t border-gray-200 pt-8">
                          <div className="flex items-center gap-3 mb-4">
                            <div className="p-2 bg-gradient-to-br from-green-500 to-teal-600 rounded-lg shadow-md">
                              <Gamepad2 className="w-6 h-6 text-white" />
                            </div>
                            <div>
                              <h3 className="text-xl font-bold text-gray-800">Mô phỏng Tương tác (GeoGebra)</h3>
                              <p className="text-sm text-gray-500">Tương tác trực tiếp với thí nghiệm ảo</p>
                            </div>
                          </div>

                          {geoGebraFiles.map((ggb) => (
                            <div key={ggb.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6 hover:shadow-md transition-shadow">
                              <GeoGebraSimulation
                                filename={GeoGebraService.getPublicPath(ggb.filename)}
                                title={ggb.title}
                                description={ggb.description}
                                showCustomControls={true}
                                width="100%"
                              />
                            </div>
                          ))}
                        </div>
                      );
                    }
                    return null;
                  })()}

                  {/* Empty State - Chỉ hiện nếu KHÔNG CÓ CẢ Video lẫn GeoGebra */}
                  {(() => {
                    const lop = parseInt(idLop || '10');
                    const chuong = parseInt(idChuong?.replace('chuong-', '').split('-')[0] || '1');
                    let bai = 1;
                    if (idBai) {
                      const match = idBai.match(/bai-(\d+)/) || idBai.match(/^(\d+)/);
                      if (match) bai = parseInt(match[1]);
                    }

                    const hasGeoGebra = GeoGebraService.getByLesson(lop, chuong, bai).length > 0;
                    const hasSystemVideo = VideoService.hasVideoForLesson(lop, chuong, bai);

                    const teacherSim = teacherSimulationContent;
                    const hasTeacherSimulation = teacherSim?.contentData?.url || teacherSim?.contentData?.items?.[0]?.url;

                    if (!hasGeoGebra && !hasSystemVideo && !hasTeacherSimulation) {
                      return (
                        <div className="bg-white p-8 rounded-lg border border-gray-200 text-center mt-4">
                          <Gamepad2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                          <h4 className="text-lg font-semibold text-gray-500 mb-2">Chưa có mô phỏng</h4>
                          <p className="text-gray-400">Nội dung mô phỏng cho bài học này đang được cập nhật.</p>
                        </div>
                      );
                    }
                    return null;
                  })()}

                </div>
              </div>
            )}

            {currentSection === 'infographic' && (
              <div className="space-y-6">
                <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-lg p-6 border border-indigo-200">
                  <h3 className="text-lg font-semibold text-indigo-900 mb-4 flex items-center">
                    <Sparkles className="w-5 h-5 mr-2" />
                    Infographic AI
                  </h3>
                  {teacherInfographicContent ? (
                    <InfographicTab content={teacherInfographicContent} />
                  ) : (
                    <div className="bg-white p-8 rounded-lg border border-indigo-100 text-center">
                      <Sparkles className="w-12 h-12 text-indigo-300 mx-auto mb-3" />
                      <p className="text-gray-500">Chưa có nội dung Infographic cho bài học này.</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <AnimatePresence>
            {summaryActive && (
              <motion.div
                key="lesson-summary-panel"
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 40 }}
                transition={{ duration: 0.3, ease: 'easeOut' }}
                className="fixed bottom-6 right-4 z-[9100] w-[clamp(18rem,92vw,24rem)]"
              >
                <div className="relative overflow-hidden rounded-2xl border border-blue-100 bg-white shadow-2xl shadow-blue-200/60">
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-cyan-50 opacity-90" />
                  <div className="relative p-4 sm:p-5">
                    <div className="mb-4 flex items-start justify-between gap-3">
                      <div>
                        <p className="text-[0.65rem] uppercase tracking-[0.25em] text-blue-500">
                          Tóm tắt cuối bài
                        </p>
                        <h4 className="text-lg font-semibold text-blue-700">Những điểm cần ghi nhớ</h4>
                      </div>
                      <motion.span
                        className="text-2xl"
                        animate={{ rotate: [0, -6, 6, 0] }}
                        transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
                      >
                        ✨
                      </motion.span>
                    </div>
                    <ul className="max-h-48 space-y-2 overflow-auto pr-1">
                      {summaryPoints.map((point, index) => {
                        const meta = summarySectionMeta[index % summarySectionMeta.length]
                        const isActive = meta?.key === activeSummarySection
                        return (
                          <li
                            key={`${meta?.key ?? 'summary'}-${index}`}
                            className={`group relative flex gap-3 rounded-xl border border-slate-200/60 bg-white/90 p-3 shadow-sm transition ${isActive
                              ? 'border-blue-400 bg-blue-50/80 shadow-blue-200'
                              : 'hover:border-blue-200 hover:bg-blue-50/40'
                              }`}
                          >
                            <div
                              className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-sm font-semibold transition ${isActive
                                ? 'bg-blue-500 text-white shadow-md shadow-blue-400/60'
                                : 'bg-blue-100 text-blue-600'
                                }`}
                            >
                              {index + 1}
                            </div>
                            <div className="space-y-1">
                              {meta?.title && (
                                <p
                                  className={`text-xs font-semibold uppercase tracking-wide ${isActive ? 'text-blue-600' : 'text-slate-500'
                                    }`}
                                >
                                  {meta.title}
                                </p>
                              )}
                              <p
                                className={`text-sm leading-relaxed ${isActive ? 'text-blue-700' : 'text-slate-700'
                                  }`}
                              >
                                {point}
                              </p>
                            </div>
                          </li>
                        )
                      })}
                    </ul>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.96 }}
                      onClick={() => {
                        setSummaryAcknowledged(true)
                        setShowSummaryPanel(false)
                      }}
                      className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-500 to-indigo-500 px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-blue-400/40 transition"
                    >
                      <motion.span
                        className="inline-flex h-6 w-6 items-center justify-center rounded-full border-2 border-white/80"
                        animate={{ scale: [1, 1.15, 1] }}
                        transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
                      >
                        ✓
                      </motion.span>
                      <span>Đánh dấu đã nắm</span>
                    </motion.button>
                    <p className="mt-3 text-[0.7rem] leading-relaxed text-slate-500">
                      Sau khi đánh dấu, bạn có thể mở lại tóm tắt ở góc dưới bên phải bất kỳ lúc nào.
                    </p>
                  </div>
                </div>
              </motion.div>
            )}
            {!summaryActive && summaryAcknowledged && currentSection === 'lyThuyet' && !isQuizFullscreen && (
              <motion.button
                key="lesson-summary-pill"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 30 }}
                transition={{ duration: 0.25, ease: 'easeOut' }}
                onClick={() => {
                  setSummaryAcknowledged(false)
                  setShowSummaryPanel(true)
                }}
                className="fixed bottom-6 right-4 z-[9000] flex items-center gap-2 rounded-full bg-blue-600/90 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-blue-500/40 backdrop-blur"
              >
                <span>📘</span>
                <span>Xem lại tóm tắt</span>
              </motion.button>
            )}
          </AnimatePresence>

          {/* Navigation Footer - Hidden, navigation moved to top */}
        </div>
      )}
      {/* Footer hidden for focused learning */}

      {/* Fullscreen Quiz Interface - Rendered outside main content */}
      {isQuizFullscreen && showQuizInterface && (bankQuizData || quizData) && (
        <QuizInterface
          key={quizKey} // Force remount khi quizKey thay đổi
          quizData={bankQuizData || quizData}
          lessonId={`lop-${idLop}-chuong-${idChuong}-bai-${idBai}`}
          onBack={() => {
            console.log('🔙 [FULLSCREEN] QuizInterface onBack called')

            // Force thoát browser fullscreen nếu đang fullscreen
            if (document.fullscreenElement) {
              document.exitFullscreen().catch(err => {
                console.log('Exit fullscreen failed:', err)
              })
            }

            setShowQuizInterface(false)
            setIsQuizFullscreen(false) // Thoát fullscreen mode
            setQuizKey(prev => prev + 1) // Tăng key để reset component lần sau
            setCurrentSection('lyThuyet') // Quay về tab lý thuyết sau khi nộp bài
            window.scrollTo({ top: 0, behavior: 'smooth' }) // Scroll lên đầu trang
          }}
          onFullscreenChange={setIsQuizFullscreen}
          forceDarkMode={true}
        />
      )}

      {/* 📊 Final Stats Modal - Chỉ hiện 1 lần sau khi hoàn thành TẤT CẢ */}
      {showFinalStatsModal && (() => {
        const stats = LessonStatsService.getStats(lessonId)
        if (!stats) {
          console.log(`❌ No stats found for lessonId: ${lessonId}`)
          return null
        }

        console.log(`🎉 Showing FINAL stats modal for lessonId: ${lessonId}`, stats)

        return (
          <LessonStatsDisplay
            stats={stats}
            duration={LessonStatsService.getDuration(lessonId)}
            accuracy={LessonStatsService.getAccuracy(lessonId)}
            progress={LessonStatsService.getProgress(lessonId)}
            onClose={() => setShowFinalStatsModal(false)}
          />
        )
      })()}

      {/* 🎮 Physics Survival Runner Game - Fullscreen Mode */}
      {showPhysicsSurvivalGame && (
        <div className="fixed inset-0 z-[9999] bg-black">
          <PhysicsSurvivalRunner
            lop={lopNumber}
            chuong={chuongNumber}
            bai={baiNumber}
            minQuestions={10}
            maxQuestions={10}
            onExit={() => {
              setShowPhysicsSurvivalGame(false)
              toast.success('🎉 Đã hoàn thành minigame! Kiến thức đã được củng cố!')
            }}
          />
        </div>
      )}

      {/* ✅ Modal yêu cầu đăng nhập với Google - Không cho đóng nếu chưa đăng nhập */}
      {showLoginModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={(e) => {
            // Không cho đóng khi click ra ngoài
            e.stopPropagation();
          }}
        >
          <div
            className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* ✅ Không hiện nút X - bắt buộc phải đăng nhập */}
            <div className="flex justify-end items-center mb-6">
              <div className="w-8 h-8"></div> {/* Spacer để giữ layout */}
            </div>

            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Yêu cầu đăng nhập</h2>
              <p className="text-gray-600 mb-6">
                Vui lòng đăng nhập với Google để xem nội dung lý thuyết và làm bài trắc nghiệm
              </p>

              {/* Google Sign-In Button Container */}
              <div ref={googleButtonRef} id="google-signin-button-xembaihoc" className="flex justify-center mb-4"></div>

              <p className="text-xs text-gray-500">
                Sau khi đăng nhập, bạn sẽ được yêu cầu bổ sung thông tin nếu cần
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default XemBaiHoc
